import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Calendar, 
  Map, 
  Download, 
  CheckCircle,
  TrendingUp,
  Users,
  DollarSign,
  Target,
  Clock,
  Shield,
  Zap,
  BarChart3,
  X,
  Server,
  Sparkles,
  Star,
  CheckCircle2,
  Phone,
  Mail,
  Edit2
} from 'lucide-react';
import SpiderChartComparison from './charts/SpiderChartComparison';
import RoadmapGantt from './charts/RoadmapGantt';
import { generatePDF, generatePDFSimple } from '../services/pdfGenerator';
import { generateHTML } from '../services/htmlGenerator';
import AIDocumentEditor from './AIDocumentEditor';
import { useAuth } from '../contexts/AuthContext';
import * as XLSX from 'xlsx';
import {
  CLIENT_2026_ALL_COL_DEFS,
  CLIENT_2026_AXE_TITLES,
  CLIENT_2026_ROWS,
  CLIENT_2026_WORKBOOK_MERGES,
  buildClient2026WorkbookHeaders,
  findClient2026MarkdownRow,
  getClient2026MarkdownColumnValue,
  parseClient2026MarkdownRow,
} from '../domain/besoins/templates/prise-de-besoin-client-2026';
import {
  CLIENT_2026_ROWS as CLIENT_2026_V3_ROWS,
  findClient2026MarkdownRow as findClient2026V3MarkdownRow,
  getClient2026MarkdownColumnValue as getClient2026V3MarkdownColumnValue,
} from '../domain/besoins/templates/prise-de-besoin-client-2026-v3';
import {
  CLIENT_2026_ALL_COL_DEFS as LEGACY_CLIENT_2026_ALL_COL_DEFS,
  CLIENT_2026_AXE_TITLES as LEGACY_CLIENT_2026_AXE_TITLES,
  CLIENT_2026_ROWS as LEGACY_CLIENT_2026_ROWS,
  buildLegacyClient2026ExportRow,
  findClient2026MarkdownRow as findLegacyClient2026MarkdownRow,
} from '../domain/besoins/templates/prise-de-besoin-client-2026-legacy';

/** Données de visualisation extraites du document Plan Directeur (générées par OpenAI à partir du client) */
export interface PlanDirecteurVizData {
  asIs: Record<string, number>;
  toBe: Record<string, number>;
  roadmap: Array<{ quarter: string; period: string; projects: number; progress: number }>;
}

const PRISME_VIZ_MARKER = '<!-- PRISME_PLAN_DIRECTEUR_VIZ -->';

function parsePlanDirecteurJson(raw: string): PlanDirecteurVizData | null {
  try {
    let s = raw.trim();
    s = s.replace(/"asls"\s*:/g, '"asIs":');
    s = s.replace(/"proiects"\s*:/g, '"projects":');
    s = s.replace(/"quarter":\s*"0?(\d)"/g, '"quarter": "Q$1"');
    s = s.replace(/"quarter":\s*"Q(\d)"\s*\.\s*"period"/g, '"quarter": "Q$1", "period"');
    s = s.replace(/"period"\s*\.\s*"([^"]+)"/g, '"period": "$1"');
    s = s.replace(/"progress"\s*\.\s*(\d+)/g, '"progress": $1');
    const data = JSON.parse(s) as {
      asIs?: Record<string, number>;
      asls?: Record<string, number>;
      toBe?: Record<string, number>;
      roadmap?: Array<{ quarter: string; period: string; projects: number; progress: number }>;
    };
    const asIs = data.asIs ?? data.asls;
    if (!asIs || !data.toBe || !Array.isArray(data.roadmap)) return null;
    return { asIs, toBe: data.toBe, roadmap: data.roadmap };
  } catch {
    return null;
  }
}

/** Extrait le bloc JSON PRISME_PLAN_DIRECTEUR_VIZ du markdown (données client pour spider + roadmap) */
export function extractPlanDirecteurVizData(markdown: string): PlanDirecteurVizData | null {
  const idx = markdown.indexOf(PRISME_VIZ_MARKER);
  if (idx === -1) return null;
  const after = markdown.slice(idx);
  // Bloc délimité par ``` ... ```
  const codeBlock = /```(?:json)?\s*([\s\S]*?)```/.exec(after);
  if (codeBlock?.[1]) {
    const parsed = parsePlanDirecteurJson(codeBlock[1]);
    if (parsed) return parsed;
  }
  // Fallback : pas de ``` fermant, chercher un objet JSON après ``` ou après le marqueur
  const jsonStart = after.search(/\{[\s\n]*"(?:asIs|asls)"\s*:/);
  if (jsonStart !== -1) {
    const fromBrace = after.slice(jsonStart);
    const parsed = parsePlanDirecteurJson(fromBrace);
    if (parsed) return parsed;
  }
  return null;
}

/** Retire le bloc PRISME_PLAN_DIRECTEUR_VIZ du markdown pour l'affichage (n'affiche jamais le JSON brut) */
export function stripPlanDirecteurVizBlock(markdown: string): string {
  const idx = markdown.indexOf(PRISME_VIZ_MARKER);
  if (idx === -1) return markdown;
  const before = markdown.slice(0, idx).trimEnd();
  const after = markdown.slice(idx);
  const openBacktick = after.indexOf('```');
  if (openBacktick === -1) return markdown;
  const afterOpen = after.slice(openBacktick + 3);
  const closeBacktick = afterOpen.indexOf('```');
  // Si pas de ``` fermant, retirer quand même tout jusqu'à la fin pour ne pas afficher le JSON brut
  const endOfBlock = closeBacktick === -1 ? afterOpen.length : closeBacktick + 3;
  const rest = afterOpen.slice(endOfBlock).replace(/^\s*\n?/, '').trimStart();
  return (before + (rest ? '\n\n' + rest : '')).trim();
}

const PRISME_ROADMAP_24M_VIZ_MARKER = '<!-- PRISME_ROADMAP_24M_VIZ -->';

/** Extrait le bloc JSON PRISME_ROADMAP_24M_VIZ du markdown (même format que Plan Directeur) */
export function extractRoadmap24VizData(markdown: string): PlanDirecteurVizData | null {
  const idx = markdown.indexOf(PRISME_ROADMAP_24M_VIZ_MARKER);
  if (idx === -1) return null;
  const after = markdown.slice(idx);
  const codeBlock = /```(?:json)?\s*([\s\S]*?)```/.exec(after);
  if (codeBlock?.[1]) {
    const parsed = parsePlanDirecteurJson(codeBlock[1]);
    if (parsed) return parsed;
  }
  const jsonStart = after.search(/\{[\s\n]*"(?:asIs|asls)"\s*:/);
  if (jsonStart !== -1) {
    const parsed = parsePlanDirecteurJson(after.slice(jsonStart));
    if (parsed) return parsed;
  }
  return null;
}

/** Retire le bloc PRISME_ROADMAP_24M_VIZ du markdown pour l'affichage */
export function stripRoadmap24VizBlock(markdown: string): string {
  const idx = markdown.indexOf(PRISME_ROADMAP_24M_VIZ_MARKER);
  if (idx === -1) return markdown;
  const before = markdown.slice(0, idx).trimEnd();
  const after = markdown.slice(idx);
  const openBacktick = after.indexOf('```');
  if (openBacktick === -1) return markdown;
  const afterOpen = after.slice(openBacktick + 3);
  const closeBacktick = afterOpen.indexOf('```');
  const endOfBlock = closeBacktick === -1 ? afterOpen.length : closeBacktick + 3;
  const rest = afterOpen.slice(endOfBlock).replace(/^\s*\n?/, '').trimStart();
  return (before + (rest ? '\n\n' + rest : '')).trim();
}

/** Données de visualisation extraites du document Plan budgétaire (générées par OpenAI à partir du client) */
export interface PlanBudgetaireVizData {
  piliers: Array<{ pilier: string; montant: number; pourcentage: number; capex: number; opex: number }>;
}

const PRISME_PLAN_BUDGETAIRE_VIZ_MARKER = '<!-- PRISME_PLAN_BUDGETAIRE_VIZ -->';

function parsePlanBudgetaireJson(raw: string): PlanBudgetaireVizData | null {
  try {
    const s = raw.trim();
    const data = JSON.parse(s) as { piliers?: Array<{ pilier?: string; montant?: number; pourcentage?: number; capex?: number; opex?: number }> };
    if (!data.piliers || !Array.isArray(data.piliers) || data.piliers.length === 0) return null;
    const piliers = data.piliers.map((p) => ({
      pilier: String(p.pilier ?? ''),
      montant: Number(p.montant) || 0,
      pourcentage: Number(p.pourcentage) || 0,
      capex: Number(p.capex) ?? 50,
      opex: Number(p.opex) ?? 50,
    }));
    return { piliers };
  } catch {
    return null;
  }
}

/** Extrait le bloc JSON PRISME_PLAN_BUDGETAIRE_VIZ du markdown */
export function extractPlanBudgetaireVizData(markdown: string): PlanBudgetaireVizData | null {
  const idx = markdown.indexOf(PRISME_PLAN_BUDGETAIRE_VIZ_MARKER);
  if (idx === -1) return null;
  const after = markdown.slice(idx);
  const codeBlock = /```(?:json)?\s*([\s\S]*?)```/.exec(after);
  if (codeBlock?.[1]) {
    const parsed = parsePlanBudgetaireJson(codeBlock[1]);
    if (parsed) return parsed;
  }
  const jsonStart = after.search(/\{[\s\n]*"piliers"\s*:/);
  if (jsonStart !== -1) {
    const parsed = parsePlanBudgetaireJson(after.slice(jsonStart));
    if (parsed) return parsed;
  }
  return null;
}

/** Retire le bloc PRISME_PLAN_BUDGETAIRE_VIZ du markdown pour l'affichage */
export function stripPlanBudgetaireVizBlock(markdown: string): string {
  const idx = markdown.indexOf(PRISME_PLAN_BUDGETAIRE_VIZ_MARKER);
  if (idx === -1) return markdown;
  const before = markdown.slice(0, idx).trimEnd();
  const after = markdown.slice(idx);
  const openBacktick = after.indexOf('```');
  if (openBacktick === -1) return markdown;
  const afterOpen = after.slice(openBacktick + 3);
  const closeBacktick = afterOpen.indexOf('```');
  const endOfBlock = closeBacktick === -1 ? afterOpen.length : closeBacktick + 3;
  const rest = afterOpen.slice(endOfBlock).replace(/^\s*\n?/, '').trimStart();
  return (before + (rest ? '\n\n' + rest : '')).trim();
}

const PRISME_CYBER_INCIDENT_COVER_START = '<!-- PRISME_CYBER_INCIDENT_COVER_JSON -->';
const PRISME_CYBER_INCIDENT_COVER_END = '<!-- /PRISME_CYBER_INCIDENT_COVER_JSON -->';

export interface CyberIncidentCoverData {
  client: string;
  dateLabel: string;
  title?: string;
}

/** Données de la page de présentation (rapport incident cyber) injectées par le serveur. */
export function extractCyberIncidentCoverData(markdown: string): CyberIncidentCoverData | null {
  const s = markdown.indexOf(PRISME_CYBER_INCIDENT_COVER_START);
  const e = markdown.indexOf(PRISME_CYBER_INCIDENT_COVER_END);
  if (s === -1 || e === -1 || e <= s) return null;
  const json = markdown.slice(s + PRISME_CYBER_INCIDENT_COVER_START.length, e).trim();
  try {
    const o = JSON.parse(json) as { client?: unknown; dateLabel?: unknown; title?: unknown };
    if (o && typeof o === 'object' && o.client != null && o.dateLabel != null) {
      const title = o.title != null && String(o.title).trim() !== '' ? String(o.title) : undefined;
      return { client: String(o.client), dateLabel: String(o.dateLabel), title };
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Retire le bloc couverture pour ne pas afficher le JSON brut. */
export function stripCyberIncidentCoverBlock(markdown: string): string {
  const s = markdown.indexOf(PRISME_CYBER_INCIDENT_COVER_START);
  const e = markdown.indexOf(PRISME_CYBER_INCIDENT_COVER_END);
  if (s === -1 || e === -1 || e <= s) return markdown;
  const after = markdown.slice(e + PRISME_CYBER_INCIDENT_COVER_END.length);
  return (markdown.slice(0, s) + after).replace(/\n{3,}/g, '\n\n').trim();
}

interface OffreServiceOption {
  id: string;
  nom: string;
  tableTitle: string;
  description: string;
  prix: number | null;
  prixLabel: string;
  piliers: number | null;
  piliersLabel: string;
  tactiques: number | null;
  tactiquesLabel: string;
  couleur: 'emerald' | 'indigo' | 'purple';
  recommandee: boolean;
  details: Array<{ label: string; value: string }>;
  piliersCouverts: string[];
  avantages: string[];
  desavantages: string[];
  repartitionEffort: Array<{ pilier: string; pourcentage: number; montant: number }>;
  spiderData: {
    asIs: Record<string, number>;
    toBe: Record<string, number>;
  };
}

function normalizeMarkdownCell(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/\\#/g, '#')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeKey(value: string): string {
  return normalizeMarkdownCell(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function parseCurrencyValue(value: string): number | null {
  const match = normalizeMarkdownCell(value).replace(/\s/g, '').match(/(\d+(?:[,.]\d+)?)/);
  if (!match) return null;
  const n = Number(match[1].replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function parseMonthlyAmountFromText(value: string): number | null {
  const normalized = normalizeMarkdownCell(value);
  const afterEquals = normalized.match(/=\s*([\d\s,.]+)\s*\$/);
  if (afterEquals) return parseCurrencyValue(afterEquals[1]);

  const matches = Array.from(normalized.matchAll(/([\d\s,.]+)\s*\$/g));
  if (matches.length === 0) return null;
  return parseCurrencyValue(matches[matches.length - 1][1]);
}

function countPiliers(value: string): number | null {
  const normalized = normalizeMarkdownCell(value);
  const rangeMatch = normalized.match(/(\d+)\s*[àa-]\s*(\d+)/i);
  if (rangeMatch) {
    const start = Number(rangeMatch[1]);
    const end = Number(rangeMatch[2]);
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      return end - start + 1;
    }
  }

  const numbers = Array.from(normalized.matchAll(/\b([1-6])\b/g)).map((m) => Number(m[1]));
  const unique = new Set(numbers);
  return unique.size > 0 ? unique.size : null;
}

function extractTactiquesForOffer(markdown: string, offerName: string): number | null {
  const offerPattern = offerName === 'Essentielle' ? '(?:ESSENTIELLE|OPTIMIS[ÉE]E)' : offerName.toUpperCase();
  const heading = new RegExp(`^\\\\?#*\\s*Offre\\s+${offerPattern}[^\\n]*$`, 'imu');
  const match = heading.exec(markdown);
  if (!match) return null;

  const sectionStart = (match.index || 0) + match[0].length;
  const nextHeading = markdown.slice(sectionStart).search(/\n\s*\\?#*\s*Offre\s+(?:ESSENTIELLE|OPTIMIS[ÉE]E|RECOMMAND[ÉE]E|COMPL[ÈE]TE)/iu);
  const section = nextHeading === -1 ? markdown.slice(sectionStart) : markdown.slice(sectionStart, sectionStart + nextHeading);
  const tactiquesMatch =
    section.match(/tactiques?\s*(?:couvertes?|activ[ée]es?)?\s*[:\-]?\s*~?\s*(\d+)/i) ||
    section.match(/~?\s*(\d+)\s+tactiques?/i);
  return tactiquesMatch ? Number(tactiquesMatch[1]) : null;
}

function extractOffreServiceOptions(markdown: string): OffreServiceOption[] | null {
  const tableBlocks = markdown.match(/(?:^\|.*\|\s*$\n?)+/gm) || [];
  const table = tableBlocks
    .map((block) => block.trim().split('\n').map((line) => line.trim()))
    .find((lines) => {
      if (lines.length < 3) return false;
      const headers = lines[0].split('|').slice(1, -1).map(normalizeKey);
      return headers[0]?.includes('element') && headers.slice(1).filter((h) => h.includes('offre')).length >= 3;
    });

  if (!table) return null;

  const headers = table[0].split('|').slice(1, -1).map(normalizeMarkdownCell);
  const rows = table.slice(2).map((line) => line.split('|').slice(1, -1).map(normalizeMarkdownCell));
  if (headers.length < 4 || rows.length === 0) return null;

  const axes = ['Gouvernance', 'Catalogue', 'Plan directeur', 'Conformité', 'Traçabilité', 'DRP'];
  const colors: Array<'emerald' | 'indigo' | 'purple'> = ['emerald', 'indigo', 'purple'];
  const offerNames = ['Essentielle', 'Recommandée', 'Complète'];

  return [1, 2, 3].map((columnIndex, idx) => {
    const details = rows
      .map((row) => ({ label: row[0], value: row[columnIndex] || '' }))
      .filter((item) => item.label && item.value && !/^[-—]+$/.test(item.value));

    const findDetail = (pattern: RegExp) => details.find((item) => pattern.test(normalizeKey(item.label)))?.value || '';
    const priceLabel = findDetail(/prix.*mensuel/) || findDetail(/investissement/) || '';
    const piliersLabel = findDetail(/piliers.*couvert/) || findDetail(/piliers/) || '';
    const tactiques = extractTactiquesForOffer(markdown, offerNames[idx]);
    const prix = parseCurrencyValue(priceLabel);
    const piliers = countPiliers(piliersLabel);

    const toBeValue = [55, 75, 90][idx];
    const optionDetails = details.filter((item) => !/prix.*mensuel|piliers.*couvert/i.test(normalizeKey(item.label)));

    return {
      id: offerNames[idx].toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, ''),
      nom: offerNames[idx],
      tableTitle: idx === 0 ? 'Offre ESSENTIELLE' : headers[columnIndex],
      description: findDetail(/intention/) || (idx === 0 ? 'Sécuriser l’essentiel' : idx === 1 ? 'Meilleur équilibre risque/coût' : 'Couverture renforcée'),
      prix,
      prixLabel: priceLabel || 'Non précisé',
      piliers,
      piliersLabel: piliersLabel || 'Non précisé',
      tactiques,
      tactiquesLabel: tactiques != null ? String(tactiques) : 'Non précisé',
      couleur: colors[idx],
      recommandee: idx === 1,
      details: optionDetails,
      piliersCouverts: piliersLabel ? [piliersLabel] : [],
      avantages: optionDetails.slice(0, 5).map((item) => `${item.label}: ${item.value}`),
      desavantages: [],
      repartitionEffort: [],
      spiderData: {
        asIs: Object.fromEntries(axes.map((axis) => [axis, 35])),
        toBe: Object.fromEntries(axes.map((axis) => [axis, toBeValue])),
      },
    };
  });
}

type EbaucheOdsComparativeRow = {
  id: string;
  scenario: string;
  price: number | null;
  priceLabel: string;
  pillars: number | null;
  pillarsLabel: string;
  services: string;
  exclusions: string;
  risks: string;
  value: string;
  color: 'emerald' | 'indigo' | 'purple';
  recommended?: boolean;
};

type EbaucheOdsComparativeTable = {
  headers: string[];
  rows: EbaucheOdsComparativeRow[];
};

type EbaucheOdsPricingTableTotal = {
  label: string;
  total: number;
};

function parseMarkdownTableLine(line: string): string[] {
  return line.split('|').slice(1, -1).map(normalizeMarkdownCell);
}

function isMarkdownSeparatorLine(line: string): boolean {
  const cells = line.split('|').slice(1, -1).map((cell) => cell.trim());
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function normalizeEbaucheOdsScenarioName(value: string): string {
  const cleaned = normalizeMarkdownCell(value)
    .replace(/[⭐★]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const key = normalizeKey(cleaned);
  if (key.includes('optimisee') || key.includes('essentielle') || key.includes('essentiel')) return 'Essentielle';
  if (key.includes('recommandee') || key.includes('recommande')) return 'Recommandée';
  if (key.includes('complete') || key.includes('complet')) return 'Complète';
  return cleaned || 'Offre';
}

function extractEbaucheOdsPricingTableTotals(markdown: string): EbaucheOdsPricingTableTotal[] {
  const tableBlocks = markdown.match(/(?:^\|.*\|\s*$\n?)+/gm) || [];

  return tableBlocks
    .map((block) => block.trim().split('\n').map((line) => line.trim()))
    .map((lines) => {
      if (lines.length < 3 || !isMarkdownSeparatorLine(lines[1])) return null;

      const headers = parseMarkdownTableLine(lines[0]);
      const normalizedHeaders = headers.map(normalizeKey);
      const itemIdx = normalizedHeaders.findIndex((header) => header.includes('item') || header.includes('titre'));
      const unitPriceIdx = normalizedHeaders.findIndex((header) => header.includes('prix') && header.includes('unitaire'));
      const unitsIdx = normalizedHeaders.findIndex((header) => header.includes('unite'));
      const totalIdx = normalizedHeaders.findIndex((header) => header.includes('total'));
      if (itemIdx < 0 || unitPriceIdx < 0 || unitsIdx < 0 || totalIdx < 0) return null;

      const rows = lines.slice(2).map(parseMarkdownTableLine).filter((row) => row.some(Boolean));
      const explicitTotalRow = rows.find((row) => {
        const label = normalizeKey(row[itemIdx] || row[0] || '');
        return label === 'total' || label.includes('total mensuel') || label.includes('total des montants');
      });

      const computedTotal = rows.reduce((sum, row) => {
        const label = normalizeKey(row[itemIdx] || row[0] || '');
        if (label === 'total' || label.includes('total mensuel') || label.includes('total des montants')) return sum;
        const value = parseCurrencyValue(row[totalIdx] || '');
        return value == null ? sum : sum + value;
      }, 0);

      const explicitTotal = explicitTotalRow ? parseCurrencyValue(explicitTotalRow[totalIdx] || '') : null;
      const total = explicitTotal != null ? explicitTotal : computedTotal;
      if (!Number.isFinite(total) || total <= 0) return null;

      return { label: headers[totalIdx] || 'Total', total };
    })
    .filter((table): table is EbaucheOdsPricingTableTotal => Boolean(table));
}

function extractEbaucheOdsAdditionalProductTotals(markdown: string): EbaucheOdsPricingTableTotal[] {
  const tableBlocks = markdown.match(/(?:^\|.*\|\s*$\n?)+/gm) || [];

  return tableBlocks
    .map((block) => block.trim().split('\n').map((line) => line.trim()))
    .map((lines) => {
      if (lines.length < 3 || !isMarkdownSeparatorLine(lines[1])) return null;

      const headers = parseMarkdownTableLine(lines[0]);
      const normalizedHeaders = headers.map(normalizeKey);
      const typeIdx = normalizedHeaders.findIndex((header) => header.includes('type'));
      const titleIdx = normalizedHeaders.findIndex((header) => header.includes('titre'));
      const contentIdx = normalizedHeaders.findIndex((header) => header.includes('contenu'));
      const statusIdx = normalizedHeaders.findIndex((header) => header.includes('statut'));
      if (typeIdx < 0 || titleIdx < 0 || contentIdx < 0 || statusIdx < 0) return null;

      const rows = lines.slice(2).map(parseMarkdownTableLine).filter((row) => row.some(Boolean));
      const computedTotal = rows.reduce((sum, row) => {
        const label = normalizeKey(row[typeIdx] || row[titleIdx] || '');
        if (label === 'total' || label.includes('total mensuel')) return sum;
        const value = parseMonthlyAmountFromText(row[contentIdx] || '');
        return value == null ? sum : sum + value / 12;
      }, 0);
      if (!Number.isFinite(computedTotal) || computedTotal <= 0) return null;

      return { label: 'Produits additionnels', total: computedTotal };
    })
    .filter((table): table is EbaucheOdsPricingTableTotal => Boolean(table));
}

function extractEbaucheOdsComparativeTable(markdown: string): EbaucheOdsComparativeTable | null {
  const tableBlocks = markdown.match(/(?:^\|.*\|\s*$\n?)+/gm) || [];
  const table = tableBlocks
    .map((block) => block.trim().split('\n').map((line) => line.trim()))
    .find((lines) => {
      if (lines.length < 3 || !isMarkdownSeparatorLine(lines[1])) return false;
      const headers = parseMarkdownTableLine(lines[0]).map(normalizeKey);
      return (
        headers.some((h) => h.includes('scenario')) &&
        headers.some((h) => h.includes('prix')) &&
        headers.some((h) => h.includes('piliers')) &&
        headers.some((h) => h.includes('services')) &&
        headers.some((h) => h.includes('risques')) &&
        headers.some((h) => h.includes('valeur'))
      );
    });

  if (!table) return null;

  const headers = parseMarkdownTableLine(table[0]);
  const normalizedHeaders = headers.map(normalizeKey);
  const indexOf = (...patterns: RegExp[]) =>
    normalizedHeaders.findIndex((header) => patterns.some((pattern) => pattern.test(header)));

  const scenarioIdx = indexOf(/scenario/);
  const priceIdx = indexOf(/prix/);
  const pillarsIdx = indexOf(/piliers/);
  const servicesIdx = indexOf(/services/);
  const exclusionsIdx = indexOf(/exclusions|limites/);
  const risksIdx = indexOf(/risques/);
  const valueIdx = indexOf(/valeur/);
  const colors: Array<'emerald' | 'indigo' | 'purple'> = ['emerald', 'indigo', 'purple'];

  const rows = table
    .slice(2)
    .map(parseMarkdownTableLine)
    .filter((row) => row.some(Boolean))
    .map((row, idx) => {
      const scenario = normalizeEbaucheOdsScenarioName(row[scenarioIdx] || '');
      const priceLabel = row[priceIdx] || 'Non précisé';
      const pillarsLabel = row[pillarsIdx] || 'Non précisé';

      return {
        id: scenario.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/[^a-z0-9]+/g, '-'),
        scenario,
        price: parseCurrencyValue(priceLabel),
        priceLabel,
        pillars: countPiliers(pillarsLabel),
        pillarsLabel,
        services: row[servicesIdx] || 'Non précisé',
        exclusions: row[exclusionsIdx] || 'Non précisé',
        risks: row[risksIdx] || 'Non précisé',
        value: row[valueIdx] || 'Non précisé',
        color: colors[idx] || 'indigo',
        recommended: normalizeKey(scenario).includes('recommand'),
      };
    })
    .slice(0, 3);

  return rows.length === 3 ? { headers, rows } : null;
}

function applyBudgetReferenceToOfferCards(
  offers: OffreServiceOption[],
  budgetReference: number | null
): OffreServiceOption[] {
  if (!budgetReference || budgetReference <= 0 || offers.length < 3) return offers;
  const multipliers = [0.75, 1, 1.5];

  return offers.map((offer, idx) => {
    const price = Math.round(budgetReference * (multipliers[idx] || 1));
    return {
      ...offer,
      prix: price,
      prixLabel: `${price.toLocaleString('fr-CA')} $ CAD / mois`,
      details: [
        { label: 'Budget de référence', value: `${budgetReference.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $ CAD / mois` },
        ...offer.details.filter((detail) => !/budget de reference/i.test(normalizeKey(detail.label))),
      ],
    };
  });
}

function buildEbaucheOdsOfferPriceLabels(budgetReference: number): string[] {
  return [0.75, 1, 1.5].map((multiplier) => {
    const price = Math.round(budgetReference * multiplier);
    return `${price.toLocaleString('fr-CA')} $/mois`;
  });
}

function applyBudgetReferenceToComparativeRows(
  headers: string[],
  rows: string[][],
  budgetReference?: number | null
): string[][] {
  if (!budgetReference || budgetReference <= 0) return rows;
  const normalizedHeaders = headers.map(normalizeKey);
  const elementIdx = normalizedHeaders.findIndex((header) => header.includes('element') || header.includes('critere'));
  const offerColumns = normalizedHeaders
    .map((header, idx) => ({ header, idx }))
    .filter(({ header }) => header.includes('offre') && (header.includes('essentielle') || header.includes('recommandee') || header.includes('complete')));
  if (elementIdx < 0 || offerColumns.length < 3) return rows;

  const labels = buildEbaucheOdsOfferPriceLabels(budgetReference);
  return rows.map((row) => {
    const label = normalizeKey(row[elementIdx] || '');
    if (!label.includes('prix') || !label.includes('mensuel')) return row;
    const next = [...row];
    offerColumns.slice(0, 3).forEach(({ idx }, offerIdx) => {
      next[idx] = labels[offerIdx];
    });
    return next;
  });
}

function mapEbaucheOdsRowsToOfferOptions(table: EbaucheOdsComparativeTable | null): OffreServiceOption[] | null {
  if (!table || table.rows.length < 3) return null;

  const colors: Array<'emerald' | 'indigo' | 'purple'> = ['emerald', 'indigo', 'purple'];
  return table.rows.slice(0, 3).map((row, idx) => ({
    id: row.id,
    nom: row.scenario,
    tableTitle: `Offre ${row.scenario.toUpperCase()}`,
    description: row.value,
    prix: row.price,
    prixLabel: row.priceLabel,
    piliers: row.pillars,
    piliersLabel: row.pillarsLabel,
    tactiques: null,
    tactiquesLabel: 'Non précisé',
    couleur: colors[idx],
    recommandee: Boolean(row.recommended),
    details: [
      { label: 'Prix mensuel', value: row.priceLabel },
      { label: 'Positionnement', value: row.value },
      { label: 'Piliers couverts (dominants)', value: row.pillarsLabel },
      { label: 'Services inclus (résumé)', value: row.services },
      { label: 'Exclusions / limites majeures', value: row.exclusions },
      { label: 'Risques mieux couverts', value: row.risks },
      { label: 'Valeur attendue', value: row.value },
    ],
    piliersCouverts: row.pillarsLabel ? [row.pillarsLabel] : [],
    avantages: [],
    desavantages: [],
    repartitionEffort: [],
    spiderData: { asIs: {}, toBe: {} },
  }));
}

interface DocumentViewerProps {
  documents: {
    cahierDeCharge: string;
    roadmap: string;
    planDirecteur?: string;
    ods?: string;
  };
  companyName: string;
  timeline: string;
  onClose: () => void;
}

type TabType = 'cahier' | 'roadmap' | 'plan' | 'ods';

export default function DocumentViewer({ documents, companyName, timeline, onClose }: DocumentViewerProps) {
  const [activeTab, setActiveTab] = useState<TabType>('cahier');
  
  // États pour les contenus éditables
  const [editableContents, setEditableContents] = useState({
    cahier: documents.cahierDeCharge,
    roadmap: documents.roadmap,
    plan: documents.planDirecteur || '',
    ods: documents.ods || ''
  });

  // État pour l'éditeur IA
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<'cahier' | 'roadmap' | 'plan-directeur' | 'ods'>('cahier');

  // Fonction pour mettre à jour le contenu d'un document
  const handleContentUpdate = (docType: 'cahier' | 'roadmap' | 'plan-directeur' | 'ods', newContent: string) => {
    const key = docType === 'plan-directeur' ? 'plan' : docType;
    setEditableContents(prev => ({
      ...prev,
      [key]: newContent
    }));
  };

  // Fonction pour ouvrir l'éditeur IA
  const openEditor = (docType: 'cahier' | 'roadmap' | 'plan-directeur' | 'ods') => {
    setEditingDocument(docType);
    setIsEditorOpen(true);
  };

  const tabs = [
    { id: 'cahier' as TabType, label: "Spécifications d'exigences d'affaires et TI", icon: FileText, color: 'blue', available: true },
    { id: 'roadmap' as TabType, label: 'Roadmap Annuel', icon: Calendar, color: 'green', available: true },
    { id: 'plan' as TabType, label: 'Plan Directeur', icon: Map, color: 'purple', available: !!documents.planDirecteur },
  ].filter(tab => tab.available);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col"
      >
        {/* Header avec gradient */}
        <div className="bg-gradient-to-r from-bz-dark via-bz-blue to-purple-600 text-white p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-3xl font-bold mb-2">Documents Générés</h2>
              <p className="text-white/90">{companyName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    relative flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap
                    ${isActive 
                      ? 'bg-white text-bz-dark shadow-lg' 
                      : 'bg-white/10 text-white hover:bg-white/20'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-white rounded-lg"
                      style={{ zIndex: -1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 'cahier' && (
                <CahierDeChargeView 
                  content={editableContents.cahier} 
                  companyName={companyName}
                  onEdit={() => openEditor('cahier')}
                />
              )}
              {activeTab === 'roadmap' && (
                <RoadmapView 
                  content={editableContents.roadmap} 
                  companyName={companyName}
                  timeline={timeline}
                  onEdit={() => openEditor('roadmap')}
                />
              )}
              {activeTab === 'plan' && editableContents.plan && (
                <PlanDirecteurView
                  content={editableContents.plan}
                  companyName={companyName}
                  onEdit={() => openEditor('plan-directeur')}
                  contentOnly
                  extractedVizData={extractPlanDirecteurVizData(editableContents.plan)}
                />
              )}
              {activeTab === 'ods' && editableContents.ods && (
                <ODSView 
                  content={editableContents.ods} 
                  companyName={companyName}
                  onEdit={() => openEditor('ods')}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Éditeur IA */}
      <AIDocumentEditor
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        documentType={editingDocument}
        currentContent={editableContents[editingDocument === 'plan-directeur' ? 'plan' : editingDocument]}
        onContentUpdate={(newContent) => handleContentUpdate(editingDocument, newContent)}
        companyName={companyName}
      />
    </div>
  );
}

// Composant pour les Spécifications d'exigences d'affaires et TI
export function CahierDeChargeView({ content, companyName, onEdit }: { content: string; companyName: string; onEdit: () => void }) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Specifications_exigences_affaires_TI_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (pdfMode === 'simple') {
      await generatePDFSimple('cahier-content', filename);
    } else {
      await generatePDF('cahier-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'cahier-content',
      `${companyName}_Specifications_exigences_affaires_TI_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  return (
    <div id="cahier-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2">Spécifications d'exigences d'affaires et TI</h1>
            <p className="text-blue-100 text-lg">{companyName}</p>
            <p className="text-blue-200 text-sm mt-2">Généré le {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-blue-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <StatCard icon={FileText} label="Pages" value="12-15" />
          <StatCard icon={CheckCircle} label="Sections" value="10" />
          <StatCard icon={Users} label="Stakeholders" value="5+" />
          <StatCard icon={Clock} label="Durée proj." value="6-12 mois" />
        </div>
      </div>

      {/* Contenu formaté */}
      <DocumentContent content={content} color="blue" />
    </div>
  );
}

// Composant pour le Roadmap
export function RoadmapView({ content, companyName, timeline, onEdit }: { content: string; companyName: string; timeline: string; onEdit: () => void }) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Roadmap_Annuel_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (pdfMode === 'simple') {
      await generatePDFSimple('roadmap-content', filename);
    } else {
      await generatePDF('roadmap-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'roadmap-content',
      `${companyName}_Roadmap_Annuel_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  // Déterminer si vue mensuelle ou trimestrielle
  const isShortTerm = timeline?.includes('Urgent') || timeline?.includes('Court terme');
  const periods = isShortTerm 
    ? [
        { id: 'p1', label: 'Mois 1-2', desc: 'Phase initiale' },
        { id: 'p2', label: 'Mois 3-4', desc: 'Déploiement' },
        { id: 'p3', label: 'Mois 5-6', desc: 'Optimisation' }
      ]
    : [
        { id: 'q1', label: 'Q1', desc: 'Jan-Mars 2025' },
        { id: 'q2', label: 'Q2', desc: 'Avr-Juin 2025' },
        { id: 'q3', label: 'Q3', desc: 'Jul-Sept 2025' },
        { id: 'q4', label: 'Q4', desc: 'Oct-Déc 2025' }
      ];

  return (
    <div id="roadmap-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2">Roadmap {isShortTerm ? 'Court Terme' : 'Annuel'}</h1>
            <p className="text-green-100 text-lg">{companyName}</p>
            <p className="text-green-200 text-sm mt-2">Plan tactique 2025 - {timeline}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-green-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-green-50 transition-all shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>

        {/* Timeline visuelle améliorée */}
        <div className="relative mt-8">
          {/* Ligne de temps */}
          <div className="absolute top-6 left-4 right-4 h-1 bg-white/30 rounded" />
          
          <div className={`grid ${periods.length === 3 ? 'grid-cols-3' : 'grid-cols-4'} gap-2`}>
            {periods.map((period, idx) => (
              <div key={period.id} className="relative">
                {/* Point sur la timeline */}
                <div className="flex justify-center mb-2">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center font-bold text-green-600 shadow-lg z-10 relative">
                    {idx + 1}
                  </div>
                </div>
                
                {/* Carte de période */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 hover:bg-white/20 transition-all">
                  <div className="text-xl font-bold text-white mb-1">{period.label}</div>
                  <div className="text-white/80 text-sm mb-3">{period.desc}</div>
                  <div className="space-y-1 text-white/90 text-xs">
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      <span>{Math.floor(Math.random() * 3) + 2} projets</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      <span>{((idx + 1) * 25)}% progression</span>
                    </div>
                  </div>
                  
                  {/* Barre de progression */}
                  <div className="mt-3 bg-white/20 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-white h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${(idx + 1) * 25}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      <RoadmapGantt
        isQuarterly={!isShortTerm}
        projects={[
          { name: 'Migration Infrastructure', start: 1, duration: 3, priority: 'high', progress: 75 },
          { name: 'Formation Équipe', start: 2, duration: 2, priority: 'medium', progress: 45 },
          { name: 'Optimisation Sécurité', start: 4, duration: 4, priority: 'high', progress: 30 },
          { name: 'Déploiement BI', start: 6, duration: 3, priority: 'medium', progress: 15 },
          { name: 'Migration Cloud', start: 8, duration: 4, priority: 'high', progress: 5 },
        ]}
      />

      {/* Contenu formaté */}
      <DocumentContent content={content} color="green" />
    </div>
  );
}

// Composant pour le Plan Directeur
// contentOnly=true : contenu généré (Gestion documentaire). Si extractedVizData est fourni, affiche spider + roadmap avec ces données client.
export function PlanDirecteurView({
  content,
  companyName,
  onEdit,
  contentOnly = false,
  extractedVizData,
}: {
  content: string;
  companyName: string;
  onEdit: () => void;
  contentOnly?: boolean;
  extractedVizData?: PlanDirecteurVizData | null;
}) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Plan_Directeur_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (pdfMode === 'simple') {
      await generatePDFSimple('plan-content', filename);
    } else {
      await generatePDF('plan-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'plan-content',
      `${companyName}_Plan_Directeur_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  // Mode Gestion documentaire : contenu généré + tableaux (toujours retirer le bloc JSON du contenu affiché)
  if (contentOnly) {
    const displayContent = stripPlanDirecteurVizBlock(content);
    const dimensions = extractedVizData
      ? Array.from(new Set([...Object.keys(extractedVizData.asIs), ...Object.keys(extractedVizData.toBe)]))
      : [];

    return (
      <div id="plan-content" className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Plan Directeur TI – Vision Stratégique (3-5 ans)</h1>
            <p className="text-sm text-gray-600 mt-1">{companyName}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700"
            >
              <Sparkles className="w-4 h-4" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              HTML
            </button>
          </div>
        </div>

        {/* Tableaux générés à partir des données du client (extraits du document) */}
        {extractedVizData && (
          <>
            <SpiderChartComparison asIs={extractedVizData.asIs} toBe={extractedVizData.toBe} />

            {/* Tableau Maturité AS-IS / TO-BE */}
            <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h2 className="text-lg font-bold text-gray-900">Maturité AS-IS / Cible (TO-BE)</h2>
                <p className="text-sm text-gray-600 mt-0.5">{companyName} – Scores sur 100</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Dimension</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">État actuel (AS-IS)</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Cible 3-5 ans (TO-BE)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dimensions.map((dim) => (
                      <tr key={dim} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="py-3 px-4 text-gray-900">{dim}</td>
                        <td className="py-3 px-4 text-right font-medium text-gray-700">
                          {extractedVizData.asIs[dim] ?? '—'}
                        </td>
                        <td className="py-3 px-4 text-right font-medium text-indigo-600">
                          {extractedVizData.toBe[dim] ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Tableau Roadmap Annuel */}
            <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h2 className="text-lg font-bold text-gray-900">Roadmap Annuel</h2>
                <p className="text-sm text-gray-600 mt-0.5">{companyName} – Plan tactique 2025</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Trimestre</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Période</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Nombre de projets</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Progression</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extractedVizData.roadmap.map((q) => (
                      <tr key={q.quarter} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="py-3 px-4 font-medium text-gray-900">{q.quarter}</td>
                        <td className="py-3 px-4 text-gray-700">{q.period}</td>
                        <td className="py-3 px-4 text-right text-gray-700">{q.projects}</td>
                        <td className="py-3 px-4 text-right font-medium text-gray-700">{q.progress} %</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        <DocumentContent content={displayContent} color="purple" />
      </div>
    );
  }

  return (
    <div id="plan-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2">Plan Directeur TI</h1>
            <p className="text-purple-100 text-lg">{companyName}</p>
            <p className="text-purple-200 text-sm mt-2">Vision stratégique 2025-2028</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-purple-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-all shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>

        {/* Vision metrics */}
        <div className="grid grid-cols-5 gap-4 mt-6">
          <StatCard icon={Target} label="Horizon" value="3-5 ans" />
          <StatCard icon={TrendingUp} label="ROI attendu" value="+120%" />
          <StatCard icon={DollarSign} label="Investment" value="$500K+" />
          <StatCard icon={Zap} label="Axes" value="5" />
          <StatCard icon={Shield} label="Conformité" value="100%" />
        </div>
      </div>

      {/* Spider Chart AS-IS vs TARGET avec axes cohérents */}
      <SpiderChartComparison
        asIs={{
          'Infrastructure & Cloud': 45,
          'Sécurité & Conformité': 55,
          'Applications & Données': 50,
          'Gouvernance & Organisation': 60,
          'Innovation & IA': 35,
        }}
        toBe={{
          'Infrastructure & Cloud': 85,
          'Sécurité & Conformité': 90,
          'Applications & Données': 85,
          'Gouvernance & Organisation': 85,
          'Innovation & IA': 75,
        }}
      />

      {/* Roadmap Annuel – synthèse visuelle */}
      <div className="mt-8 space-y-6">
        <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white rounded-2xl p-6 shadow-xl">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold mb-1">Roadmap Annuel</h2>
              <p className="text-green-100 text-sm">{companyName}</p>
              <p className="text-green-200 text-xs mt-1">
                Plan tactique 2025 – Moyen terme (6-12 mois)
              </p>
            </div>
          </div>

          {/* Timeline Q1–Q4 */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            {[
              { id: 'Q1', label: 'Q1', period: 'Jan–Mars 2025', projects: '2 projets', progress: 25 },
              { id: 'Q2', label: 'Q2', period: 'Avr–Juin 2025', projects: '4 projets', progress: 50 },
              { id: 'Q3', label: 'Q3', period: 'Juil–Sept 2025', projects: '3 projets', progress: 75 },
              { id: 'Q4', label: 'Q4', period: 'Oct–Déc 2025', projects: '4 projets', progress: 100 },
            ].map((q, idx) => (
              <div key={q.id} className="bg-white/5 rounded-lg p-4 border border-white/10 flex flex-col gap-2">
                <div className="flex items-center justify-between text-xs text-white/80">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white text-green-700 font-bold mr-1">
                    {idx + 1}
                  </span>
                  <span className="font-semibold">{q.label}</span>
                  <span className="text-white/60 text-[10px]">{q.period}</span>
                </div>
                <div className="text-[11px] text-white/80 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  <span>{q.projects}</span>
                </div>
                <div className="mt-1 bg-white/20 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-white h-2 rounded-full"
                    style={{ width: `${q.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline des projets (Gantt) */}
        <RoadmapGantt
          isQuarterly
          projects={[
            { name: 'Migration Infrastructure', start: 1, duration: 3, priority: 'high', progress: 75 },
            { name: 'Formation Équipe', start: 2, duration: 2, priority: 'medium', progress: 45 },
            { name: 'Optimisation Sécurité', start: 4, duration: 4, priority: 'high', progress: 30 },
            { name: 'Déploiement BI', start: 6, duration: 3, priority: 'medium', progress: 15 },
            { name: 'Migration Cloud', start: 8, duration: 4, priority: 'high', progress: 5 },
          ]}
        />
      </div>

      {/* Axes Stratégiques avec icônes */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Axes Stratégiques</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AxeCard
            icon={<Server className="w-8 h-8" />}
            title="Infrastructure & Cloud"
            description="Modernisation et migration vers le cloud"
            progress={45}
            color="blue"
          />
          <AxeCard
            icon={<Shield className="w-8 h-8" />}
            title="Sécurité & Conformité"
            description="Renforcement de la posture cybersécurité"
            progress={60}
            color="red"
          />
          <AxeCard
            icon={<BarChart3 className="w-8 h-8" />}
            title="Applications & Données"
            description="Optimisation des systèmes d'affaires"
            progress={50}
            color="green"
          />
          <AxeCard
            icon={<Zap className="w-8 h-8" />}
            title="Innovation & IA"
            description="Intégration de l'intelligence artificielle"
            progress={35}
            color="yellow"
          />
          <AxeCard
            icon={<Users className="w-8 h-8" />}
            title="Gouvernance & Organisation"
            description="Structure, rôles, équipe TI et modèle de gouvernance"
            progress={65}
            color="purple"
          />
        </div>
      </div>

      {/* Contenu formaté */}
      <DocumentContent content={content} color="purple" />
    </div>
  );
}

// Composant pour la Feuille de route TI 12–24 mois (ROADMAP_24M)
// contentOnly + extractedVizData : mode Gestion documentaire – tableaux (spider, maturité, roadmap) au début, bloc JSON masqué.
export function Roadmap24View({
  content,
  companyName,
  onEdit,
  contentOnly = false,
  extractedVizData,
}: {
  content: string;
  companyName: string;
  onEdit: () => void;
  contentOnly?: boolean;
  extractedVizData?: PlanDirecteurVizData | null;
}) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Feuille_de_route_TI_12-24_mois_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (pdfMode === 'simple') {
      await generatePDFSimple('roadmap24-content', filename);
    } else {
      await generatePDF('roadmap24-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'roadmap24-content',
      `${companyName}_Feuille_de_route_TI_12-24_mois_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  // Mode Gestion documentaire : tableaux (données client) au début, contenu sans bloc JSON
  if (contentOnly) {
    const displayContent = stripRoadmap24VizBlock(content);
    const dimensions = extractedVizData
      ? Array.from(new Set([...Object.keys(extractedVizData.asIs), ...Object.keys(extractedVizData.toBe)]))
      : [];

    return (
      <div id="roadmap24-content" className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Feuille de route TI (12-24 mois)</h1>
            <p className="text-sm text-gray-600 mt-1">{companyName}</p>
            <p className="text-xs text-gray-500 mt-0.5">Exécution tactique alignée sur le Plan Directeur TI</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-700"
            >
              <Sparkles className="w-4 h-4" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              HTML
            </button>
          </div>
        </div>

        {extractedVizData && (
          <>
            <SpiderChartComparison asIs={extractedVizData.asIs} toBe={extractedVizData.toBe} />

            <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h2 className="text-lg font-bold text-gray-900">Maturité AS-IS / Cible (TO-BE)</h2>
                <p className="text-sm text-gray-600 mt-0.5">{companyName} – Scores sur 100 (horizon 12-24 mois)</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Dimension</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">État actuel (AS-IS)</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Cible 12-24 mois (TO-BE)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dimensions.map((dim) => (
                      <tr key={dim} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="py-3 px-4 text-gray-900">{dim}</td>
                        <td className="py-3 px-4 text-right font-medium text-gray-700">
                          {extractedVizData.asIs[dim] ?? '—'}
                        </td>
                        <td className="py-3 px-4 text-right font-medium text-emerald-600">
                          {extractedVizData.toBe[dim] ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h2 className="text-lg font-bold text-gray-900">Roadmap par trimestre</h2>
                <p className="text-sm text-gray-600 mt-0.5">{companyName} – Initiatives 12-24 mois</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Trimestre</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Période</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Nombre d'initiatives</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Progression</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extractedVizData.roadmap.map((q) => (
                      <tr key={q.quarter} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="py-3 px-4 font-medium text-gray-900">{q.quarter}</td>
                        <td className="py-3 px-4 text-gray-700">{q.period}</td>
                        <td className="py-3 px-4 text-right text-gray-700">{q.projects}</td>
                        <td className="py-3 px-4 text-right font-medium text-gray-700">{q.progress} %</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        <DocumentContent content={displayContent} color="blue" />
      </div>
    );
  }

  return (
    <div id="roadmap24-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-emerald-600 to-bz-blue text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">Feuille de route TI 12–24 mois</h1>
            <p className="text-emerald-100 text-lg">{companyName}</p>
            <p className="text-emerald-200 text-sm mt-1">
              Exécution tactique alignée sur le Plan Directeur TI
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-emerald-500 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-emerald-600 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-emerald-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-emerald-700 px-5 py-2.5 rounded-lg font-semibold hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl text-sm"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-bz-blue px-5 py-2.5 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>

        {/* Stats haute niveau */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <StatCard icon={Calendar} label="Horizon" value="12–24 mois" />
          <StatCard icon={TrendingUp} label="Initiatives clés" value="8–15" />
          <StatCard icon={BarChart3} label="Piliers couverts" value="6" />
          <StatCard icon={Zap} label="Quick wins" value="3–5" />
        </div>
      </div>

      {/* Timeline & Gantt visuel */}
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-md border border-emerald-100 p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-emerald-900">Vue par trimestres</h2>
              <p className="text-xs text-emerald-700">
                Synthèse des grands chantiers par trimestre sur 12–24 mois
              </p>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
            {[
              { id: 'T1', label: 'T0–6 mois', period: 'Stabiliser & quick wins', progress: 35 },
              { id: 'T2', label: '6–12 mois', period: 'Projets structurants', progress: 60 },
              { id: 'T3', label: '12–18 mois', period: 'Industrialisation', progress: 80 },
              { id: 'T4', label: '18–24 mois', period: 'Optimisation & IA', progress: 100 },
            ].map((t, idx) => (
              <div
                key={t.id}
                className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-100 flex flex-col gap-2"
              >
                <div className="flex items-center justify-between text-xs text-emerald-900">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white font-bold mr-1">
                    {idx + 1}
                  </span>
                  <span className="font-semibold">{t.label}</span>
                  <span className="text-emerald-700 text-[10px]">{t.period}</span>
                </div>
                <div className="text-[11px] text-emerald-800 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  <span>Chantiers priorisés</span>
                </div>
                <div className="mt-1 bg-emerald-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-emerald-600 h-2 rounded-full"
                    style={{ width: `${t.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Gantt */}
        <RoadmapGantt
          isQuarterly
          projects={[
            { name: 'Stabilisation infrastructure', start: 1, duration: 2, priority: 'high', progress: 40 },
            { name: 'Modernisation sécurité & conformité', start: 2, duration: 3, priority: 'high', progress: 25 },
            { name: 'Projets applicatifs métier', start: 3, duration: 4, priority: 'medium', progress: 10 },
            { name: 'Data & BI', start: 4, duration: 3, priority: 'medium', progress: 5 },
            { name: 'Innovation & IA pilotes', start: 5, duration: 3, priority: 'low', progress: 0 },
          ]}
        />
      </div>

      {/* Contenu détaillé généré par l'IA */}
      <DocumentContent content={content} color="blue" />
    </div>
  );
}

// Cadre d’objectifs stratégiques TI & KPIs
export function ObjectifsKPIView({
  content,
  companyName,
  onEdit,
}: {
  content: string;
  companyName: string;
  onEdit: () => void;
}) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Objectifs_TI_KPIs_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (pdfMode === 'simple') {
      await generatePDFSimple('objectifs-kpi-content', filename);
    } else {
      await generatePDF('objectifs-kpi-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'objectifs-kpi-content',
      `${companyName}_Objectifs_TI_KPIs_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  return (
    <div id="objectifs-kpi-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-bz-blue to-indigo-700 text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">Cadre d’objectifs stratégiques TI & KPIs</h1>
            <p className="text-blue-100 text-lg">{companyName}</p>
            <p className="text-blue-200 text-sm mt-1">
              Alignement TI ↔ affaires & tableau de bord de performance
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-indigo-500 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-indigo-600 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-indigo-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-indigo-700 px-5 py-2.5 rounded-lg font-semibold hover:bg-indigo-50 transition-all shadow-lg hover:shadow-xl text-sm"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-bz-blue px-5 py-2.5 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>

        {/* KPIs summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <StatCard icon={Target} label="Objectifs majeurs" value="6–10" />
          <StatCard icon={BarChart3} label="KPIs clés" value="12–20" />
          <StatCard icon={Clock} label="Fréquence revue" value="Mensuel / Trimestriel" />
          <StatCard icon={Users} label="Pilotes" value="DTI + Métiers" />
        </div>
      </div>

      {/* Mini dashboard visuel */}
      <div className="bg-white rounded-2xl shadow-md border border-indigo-100 p-6 space-y-4">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h2 className="text-lg font-bold text-indigo-900">Vue synthèse du tableau de bord TI</h2>
            <p className="text-xs text-indigo-700">
              Exemple de regroupement des KPIs par pilier pour une lecture DG / DTI
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 space-y-1">
            <p className="font-semibold text-indigo-900 text-xs">Disponibilité & Qualité de service</p>
            <p className="text-indigo-700">• Disponibilité services critiques (%)</p>
            <p className="text-indigo-700">• Temps de rétablissement moyen (MTTR)</p>
            <p className="text-indigo-700">• Volume incidents récurrents</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 space-y-1">
            <p className="font-semibold text-emerald-900 text-xs">Sécurité & Conformité</p>
            <p className="text-emerald-700">• Incidents de sécurité majeurs</p>
            <p className="text-emerald-700">• Couverture sauvegardes / plans de reprise</p>
            <p className="text-emerald-700">• Avancement plan Loi 25</p>
          </div>
          <div className="bg-purple-50 border border-purple-100 rounded-xl p-3 space-y-1">
            <p className="font-semibold text-purple-900 text-xs">Projets & Innovation</p>
            <p className="text-purple-700">• Avancement Roadmap TI (%)</p>
            <p className="text-purple-700">• Projets livrés à temps / budget</p>
            <p className="text-purple-700">• Initiatives IA / data en production</p>
          </div>
        </div>
      </div>

      {/* Contenu détaillé généré par l'IA */}
      <DocumentContent content={content} color="indigo" />
    </div>
  );
}

// Matrice de responsabilité TI / client (RACI)
export function MatriceRACIView({
  content,
  companyName,
  onEdit,
}: {
  content: string;
  companyName: string;
  onEdit: () => void;
}) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Matrice_RACI_TI_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (pdfMode === 'simple') {
      await generatePDFSimple('raci-content', filename);
    } else {
      await generatePDF('raci-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'raci-content',
      `${companyName}_Matrice_RACI_TI_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  const roles = ['DG client', 'DTI / Resp. TI', 'Resp. métiers', 'BZ – Partenaire TI', 'BZ – Opérations'];
  const activities = [
    { label: 'Comité de gouvernance TI', domain: 'Gouvernance', level: 'Stratégique' },
    { label: 'Définition des priorités TI annuelles', domain: 'Gouvernance', level: 'Stratégique' },
    { label: 'Validation Plan Directeur TI', domain: 'Gouvernance', level: 'Stratégique' },
    { label: 'Lancement d’un projet TI', domain: 'Projets', level: 'Tactique' },
    { label: 'Validation des requis', domain: 'Projets', level: 'Tactique' },
    { label: 'Gestion des changements majeurs', domain: 'Projets', level: 'Tactique' },
    { label: 'Gestion des incidents majeurs', domain: 'Opérations', level: 'Opérationnel' },
    { label: 'Gestion des demandes de service', domain: 'Opérations', level: 'Opérationnel' },
    { label: 'Gestion incidents de sécurité', domain: 'Sécurité', level: 'Opérationnel' },
    { label: 'Revue Loi 25 / confidentialité', domain: 'Sécurité', level: 'Tactique' },
  ];

  const raciColors: Record<string, string> = {
    R: 'bg-emerald-600 text-white',
    A: 'bg-blue-600 text-white',
    C: 'bg-amber-500 text-white',
    I: 'bg-gray-200 text-gray-800',
  };

  return (
    <div id="raci-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-emerald-600 to-bz-blue text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">Matrice de responsabilité TI / client (RACI)</h1>
            <p className="text-emerald-100 text-lg">{companyName}</p>
            <p className="text-emerald-200 text-sm mt-1">
              Gouvernance TI clarifiée entre BZ, client et métiers
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-emerald-500 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-emerald-600 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-emerald-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-emerald-700 px-5 py-2.5 rounded-lg font-semibold hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl text-sm"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-bz-blue px-5 py-2.5 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>

        {/* Légende RACI */}
        <div className="grid grid-cols-4 gap-3 mt-4 text-xs">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${raciColors.R}`}>R</span>
            <span>Responsable</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${raciColors.A}`}>A</span>
            <span>Autorité / Approuve</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${raciColors.C}`}>C</span>
            <span>Consulté</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${raciColors.I}`}>I</span>
            <span>Informé</span>
          </div>
        </div>
      </div>

      {/* Matrice visuelle */}
      <div className="bg-white rounded-2xl shadow-md border border-emerald-100 p-4 overflow-x-auto">
        <table className="min-w-full border-collapse text-[11px]">
          <thead>
            <tr>
              <th className="border-b border-emerald-200 px-3 py-2 text-left text-emerald-900 font-semibold bg-emerald-50 sticky left-0 z-10">
                Activité
              </th>
              {roles.map((role) => (
                <th
                  key={role}
                  className="border-b border-emerald-200 px-3 py-2 text-center text-emerald-900 font-semibold bg-emerald-50"
                >
                  {role}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activities.map((act, idx) => (
              <tr key={act.label} className={idx % 2 === 0 ? 'bg-white' : 'bg-emerald-50/40'}>
                <td className="border-b border-emerald-100 px-3 py-2 text-left text-gray-800 sticky left-0 bg-inherit z-10">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">
                        {act.domain}
                      </div>
                      <div className="text-[11px]">{act.label}</div>
                    </div>
                    {act.level && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                        {act.level}
                      </span>
                    )}
                  </div>
                </td>
                {roles.map((roleIdx) => {
                  // Distribuer un pattern RACI simple juste pour le visuel par défaut
                  const letters = ['R', 'A', 'C', 'I'];
                  const letter = letters[(idx + roles.indexOf(roleIdx)) % letters.length];
                  return (
                    <td
                      key={roleIdx}
                      className="border-b border-emerald-100 px-2 py-2 text-center align-middle"
                    >
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold ${raciColors[letter]}`}
                      >
                        {letter}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Contenu détaillé généré par l'IA */}
      <DocumentContent content={content} color="emerald" />
    </div>
  );
}

// Composant pour le Plan budgétaire TI annuel (N+1)
// contentOnly + extractedVizData : mode Gestion documentaire / Livrables créés – tableaux avec données client, pas de mock
export function PlanBudgetaireView({
  content,
  companyName,
  onEdit,
  contentOnly = false,
  extractedVizData,
}: {
  content: string;
  companyName: string;
  onEdit: () => void;
  contentOnly?: boolean;
  extractedVizData?: PlanBudgetaireVizData | null;
}) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Plan_Budgetaire_TI_N1_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (pdfMode === 'simple') {
      // Version simple (comme ChateauLaurier)
      await generatePDFSimple('budget-content', filename);
    } else {
      // Version avancée (avec options et optimisations)
      await generatePDF('budget-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'budget-content',
      `${companyName}_Plan_Budgetaire_TI_N1_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  // Données client extraites du document (Livrables) ou fallback mock pour la prévisualisation type
  const budgetPiliers = extractedVizData?.piliers?.length
    ? extractedVizData.piliers
    : [
        { pilier: 'Infrastructure & Cloud', montant: 450000, pourcentage: 30, capex: 60, opex: 40 },
        { pilier: 'Sécurité & Conformité', montant: 280000, pourcentage: 19, capex: 40, opex: 60 },
        { pilier: 'Applications & Données', montant: 320000, pourcentage: 21, capex: 50, opex: 50 },
        { pilier: 'Gouvernance & Organisation', montant: 180000, pourcentage: 12, capex: 20, opex: 80 },
        { pilier: 'Support & Opérations', montant: 200000, pourcentage: 13, capex: 10, opex: 90 },
        { pilier: 'Innovation & IA', montant: 70000, pourcentage: 5, capex: 70, opex: 30 },
      ];

  const totalBudget = budgetPiliers.reduce((sum, p) => sum + p.montant, 0);
  const totalCapex = budgetPiliers.reduce((sum, p) => sum + (p.montant * p.capex) / 100, 0);
  const totalOpex = budgetPiliers.reduce((sum, p) => sum + (p.montant * p.opex) / 100, 0);
  const displayContent = contentOnly ? stripPlanBudgetaireVizBlock(content) : content;

  // Mode Gestion documentaire / Livrables créés : en-tête compact + tableaux données client + contenu sans bloc JSON
  if (contentOnly) {
    return (
      <div id="budget-content" className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Plan budgétaire TI annuel (N+1)</h1>
            <p className="text-sm text-gray-600 mt-1">{companyName}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Budget TI structuré par piliers – Année {new Date().getFullYear() + 1}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700"
            >
              <Sparkles className="w-4 h-4" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              HTML
            </button>
          </div>
        </div>

        {extractedVizData?.piliers?.length ? (
          <>
            {/* Répartition par piliers */}
            <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h2 className="text-lg font-bold text-gray-900">Répartition budgétaire par pilier TI</h2>
              </div>
              <div className="p-5 space-y-4">
                {budgetPiliers.map((p) => (
                  <div key={p.pilier} className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-semibold text-gray-800">{p.pilier}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-600">${(p.montant / 1000).toFixed(0)}K</span>
                        <span className="text-purple-600 font-bold">{p.pourcentage}%</span>
                      </div>
                    </div>
                    <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full"
                        style={{ width: `${Math.min(p.pourcentage, 100)}%` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-between px-2 text-[10px] text-white font-semibold">
                        <span>CAPEX {p.capex}%</span>
                        <span>OPEX {p.opex}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Tableau synthèse CAPEX/OPEX */}
            <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h2 className="text-lg font-bold text-gray-900">Synthèse CAPEX / OPEX par pilier</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border-b border-purple-200 px-4 py-3 text-left text-purple-900 font-semibold">Pilier TI</th>
                      <th className="border-b border-purple-200 px-4 py-3 text-right text-purple-900 font-semibold">Budget total</th>
                      <th className="border-b border-purple-200 px-4 py-3 text-right text-purple-900 font-semibold">CAPEX</th>
                      <th className="border-b border-purple-200 px-4 py-3 text-right text-purple-900 font-semibold">OPEX</th>
                      <th className="border-b border-purple-200 px-4 py-3 text-right text-purple-900 font-semibold">% du total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {budgetPiliers.map((p, idx) => (
                      <tr key={p.pilier} className={idx % 2 === 0 ? 'bg-white' : 'bg-purple-50/30'}>
                        <td className="border-b border-purple-100 px-4 py-3 text-gray-800 font-medium">{p.pilier}</td>
                        <td className="border-b border-purple-100 px-4 py-3 text-right text-gray-700">${(p.montant / 1000).toFixed(0)}K</td>
                        <td className="border-b border-purple-100 px-4 py-3 text-right text-indigo-600 font-semibold">${((p.montant * p.capex) / 100 / 1000).toFixed(0)}K</td>
                        <td className="border-b border-purple-100 px-4 py-3 text-right text-purple-600 font-semibold">${((p.montant * p.opex) / 100 / 1000).toFixed(0)}K</td>
                        <td className="border-b border-purple-100 px-4 py-3 text-right text-gray-700">{p.pourcentage}%</td>
                      </tr>
                    ))}
                    <tr className="bg-purple-100 font-bold">
                      <td className="border-t-2 border-purple-300 px-4 py-3 text-purple-900">TOTAL</td>
                      <td className="border-t-2 border-purple-300 px-4 py-3 text-right text-purple-900">${(totalBudget / 1000).toFixed(0)}K</td>
                      <td className="border-t-2 border-purple-300 px-4 py-3 text-right text-indigo-700">${(totalCapex / 1000).toFixed(0)}K</td>
                      <td className="border-t-2 border-purple-300 px-4 py-3 text-right text-purple-700">${(totalOpex / 1000).toFixed(0)}K</td>
                      <td className="border-t-2 border-purple-300 px-4 py-3 text-right text-purple-900">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : null}

        <DocumentContent content={displayContent} color="purple" />
      </div>
    );
  }

  return (
    <div id="budget-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section (mode prévisualisation type) */}
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">Plan budgétaire TI annuel (N+1)</h1>
            <p className="text-purple-100 text-lg">{companyName}</p>
            <p className="text-purple-200 text-sm mt-1">
              Budget TI structuré par piliers – Année {new Date().getFullYear() + 1}
            </p>
          </div>
          <div className="flex gap-3 items-center">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-purple-500 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-purple-600 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-purple-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-purple-700 px-5 py-2.5 rounded-lg font-semibold hover:bg-purple-50 transition-all shadow-lg hover:shadow-xl text-sm"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-indigo-600 px-5 py-2.5 rounded-lg font-semibold hover:bg-indigo-50 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <StatCard icon={DollarSign} label="Budget total N+1" value={`$${(totalBudget / 1000).toFixed(0)}K`} />
          <StatCard icon={TrendingUp} label="CAPEX" value={`$${(totalCapex / 1000).toFixed(0)}K`} />
          <StatCard icon={BarChart3} label="OPEX" value={`$${(totalOpex / 1000).toFixed(0)}K`} />
          <StatCard icon={Target} label="Piliers couverts" value={String(budgetPiliers.length)} />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md border border-purple-100 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Répartition budgétaire par pilier TI</h2>
        <div className="space-y-4">
          {budgetPiliers.map((p) => (
            <div key={p.pilier} className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="font-semibold text-gray-800">{p.pilier}</span>
                <div className="flex items-center gap-3">
                  <span className="text-gray-600">${(p.montant / 1000).toFixed(0)}K</span>
                  <span className="text-purple-600 font-bold">{p.pourcentage}%</span>
                </div>
              </div>
              <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="absolute left-0 top-0 h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full transition-all duration-1000"
                  style={{ width: `${p.pourcentage}%` }}
                />
                <div className="absolute inset-0 flex items-center justify-between px-2 text-[10px] text-white font-semibold">
                  <span>CAPEX {p.capex}%</span>
                  <span>OPEX {p.opex}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md border border-purple-100 p-6 overflow-x-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Synthèse CAPEX / OPEX par pilier</h2>
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-purple-50">
              <th className="border-b border-purple-200 px-4 py-3 text-left text-purple-900 font-semibold">Pilier TI</th>
              <th className="border-b border-purple-200 px-4 py-3 text-right text-purple-900 font-semibold">Budget total</th>
              <th className="border-b border-purple-200 px-4 py-3 text-right text-purple-900 font-semibold">CAPEX</th>
              <th className="border-b border-purple-200 px-4 py-3 text-right text-purple-900 font-semibold">OPEX</th>
              <th className="border-b border-purple-200 px-4 py-3 text-right text-purple-900 font-semibold">% du total</th>
            </tr>
          </thead>
          <tbody>
            {budgetPiliers.map((p, idx) => (
              <tr key={p.pilier} className={idx % 2 === 0 ? 'bg-white' : 'bg-purple-50/30'}>
                <td className="border-b border-purple-100 px-4 py-3 text-gray-800 font-medium">{p.pilier}</td>
                <td className="border-b border-purple-100 px-4 py-3 text-right text-gray-700">${(p.montant / 1000).toFixed(0)}K</td>
                <td className="border-b border-purple-100 px-4 py-3 text-right text-indigo-600 font-semibold">${((p.montant * p.capex) / 100 / 1000).toFixed(0)}K</td>
                <td className="border-b border-purple-100 px-4 py-3 text-right text-purple-600 font-semibold">${((p.montant * p.opex) / 100 / 1000).toFixed(0)}K</td>
                <td className="border-b border-purple-100 px-4 py-3 text-right text-gray-700">{p.pourcentage}%</td>
              </tr>
            ))}
            <tr className="bg-purple-100 font-bold">
              <td className="border-t-2 border-purple-300 px-4 py-3 text-purple-900">TOTAL</td>
              <td className="border-t-2 border-purple-300 px-4 py-3 text-right text-purple-900">${(totalBudget / 1000).toFixed(0)}K</td>
              <td className="border-t-2 border-purple-300 px-4 py-3 text-right text-indigo-700">${(totalCapex / 1000).toFixed(0)}K</td>
              <td className="border-t-2 border-purple-300 px-4 py-3 text-right text-purple-700">${(totalOpex / 1000).toFixed(0)}K</td>
              <td className="border-t-2 border-purple-300 px-4 py-3 text-right text-purple-900">100%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <DocumentContent content={displayContent} color="purple" />
    </div>
  );
}

// Composant pour le Registre d'amélioration continue (backlog TI)
export function RegistreAmeliorationContinueView({
  content,
  companyName,
  onEdit,
}: {
  content: string;
  companyName: string;
  onEdit: () => void;
}) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Registre_Amelioration_Continue_TI_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (pdfMode === 'simple') {
      await generatePDFSimple('backlog-content', filename);
    } else {
      await generatePDF('backlog-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'backlog-content',
      `${companyName}_Registre_Amelioration_Continue_TI_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  // Données mock pour le visuel (sera remplacé par parsing du markdown si besoin)
  const backlogItems = [
    { id: 1, amélioration: 'Modernisation infrastructure serveurs', pilier: 'Infrastructure & Cloud', priorité: 'Haute', effort: 'Élevé', valeur: 'Élevée', statut: 'À faire' },
    { id: 2, amélioration: 'Formation équipe cybersécurité', pilier: 'Sécurité & Conformité', priorité: 'Haute', effort: 'Moyen', valeur: 'Élevée', statut: 'En cours' },
    { id: 3, amélioration: 'Optimisation base de données', pilier: 'Applications & Données', priorité: 'Moyenne', effort: 'Moyen', valeur: 'Moyenne', statut: 'À faire' },
    { id: 4, amélioration: 'Mise en place comité TI', pilier: 'Gouvernance & Organisation', priorité: 'Haute', effort: 'Faible', valeur: 'Élevée', statut: 'À faire' },
    { id: 5, amélioration: 'Automatisation sauvegardes', pilier: 'Support & Opérations', priorité: 'Moyenne', effort: 'Faible', valeur: 'Moyenne', statut: 'Terminé' },
    { id: 6, amélioration: 'POC Intelligence Artificielle', pilier: 'Innovation & IA', priorité: 'Basse', effort: 'Élevé', valeur: 'Moyenne', statut: 'À faire' },
  ];

  const parPilier = backlogItems.reduce((acc, item) => {
    if (!acc[item.pilier]) acc[item.pilier] = [];
    acc[item.pilier].push(item);
    return acc;
  }, {} as Record<string, typeof backlogItems>);

  const parPriorité = backlogItems.reduce((acc, item) => {
    if (!acc[item.priorité]) acc[item.priorité] = [];
    acc[item.priorité].push(item);
    return acc;
  }, {} as Record<string, typeof backlogItems>);

  const getPriorityColor = (p: string) => {
    if (p === 'Haute') return 'bg-red-100 text-red-800 border-red-200';
    if (p === 'Moyenne') return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getEffortColor = (e: string) => {
    if (e === 'Élevé') return 'bg-red-500';
    if (e === 'Moyen') return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getStatusColor = (s: string) => {
    if (s === 'Terminé') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (s === 'En cours') return 'bg-blue-100 text-blue-800 border-blue-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div id="backlog-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-orange-600 to-red-600 text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">Registre d'amélioration continue (backlog TI)</h1>
            <p className="text-orange-100 text-lg">{companyName}</p>
            <p className="text-orange-200 text-sm mt-1">
              Améliorations TI prioritaires structurées par piliers
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-orange-500 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-orange-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-orange-700 px-5 py-2.5 rounded-lg font-semibold hover:bg-orange-50 transition-all shadow-lg hover:shadow-xl text-sm"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-red-600 px-5 py-2.5 rounded-lg font-semibold hover:bg-red-50 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>

        {/* Stats haute niveau */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <StatCard icon={FileText} label="Total améliorations" value={backlogItems.length.toString()} />
          <StatCard icon={Target} label="Priorité haute" value={parPriorité['Haute']?.length.toString() || '0'} />
          <StatCard icon={CheckCircle} label="Terminé" value={backlogItems.filter(i => i.statut === 'Terminé').length.toString()} />
          <StatCard icon={BarChart3} label="Piliers couverts" value={Object.keys(parPilier).length.toString()} />
        </div>
      </div>

      {/* Vue par priorité */}
      <div className="bg-white rounded-2xl shadow-md border border-orange-100 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Répartition par priorité</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {['Haute', 'Moyenne', 'Basse'].map((prio) => {
            const items = parPriorité[prio] || [];
            return (
              <div key={prio} className="border-2 rounded-lg p-4" style={{ borderColor: prio === 'Haute' ? '#ef4444' : prio === 'Moyenne' ? '#f59e0b' : '#6b7280' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold border ${getPriorityColor(prio)}`}>
                    {prio}
                  </span>
                  <span className="text-2xl font-bold text-gray-800">{items.length}</span>
                </div>
                <div className="space-y-1 mt-2">
                  {items.slice(0, 3).map((item) => (
                    <div key={item.id} className="text-xs text-gray-600 truncate">{item.amélioration}</div>
                  ))}
                  {items.length > 3 && <div className="text-xs text-gray-400">+{items.length - 3} autres</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Backlog détaillé par pilier */}
      <div className="bg-white rounded-2xl shadow-md border border-orange-100 p-6 overflow-x-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Backlog détaillé par pilier TI</h2>
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-orange-50">
              <th className="border-b border-orange-200 px-4 py-3 text-left text-orange-900 font-semibold">
                Amélioration
              </th>
              <th className="border-b border-orange-200 px-4 py-3 text-left text-orange-900 font-semibold">
                Pilier
              </th>
              <th className="border-b border-orange-200 px-4 py-3 text-center text-orange-900 font-semibold">
                Priorité
              </th>
              <th className="border-b border-orange-200 px-4 py-3 text-center text-orange-900 font-semibold">
                Effort
              </th>
              <th className="border-b border-orange-200 px-4 py-3 text-center text-orange-900 font-semibold">
                Valeur
              </th>
              <th className="border-b border-orange-200 px-4 py-3 text-center text-orange-900 font-semibold">
                Statut
              </th>
            </tr>
          </thead>
          <tbody>
            {backlogItems.map((item, idx) => (
              <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-orange-50/30'}>
                <td className="border-b border-orange-100 px-4 py-3 text-gray-800 font-medium">{item.amélioration}</td>
                <td className="border-b border-orange-100 px-4 py-3 text-gray-700 text-xs">{item.pilier}</td>
                <td className="border-b border-orange-100 px-4 py-3 text-center">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(item.priorité)}`}>
                    {item.priorité}
                  </span>
                </td>
                <td className="border-b border-orange-100 px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <div className={`w-3 h-3 rounded-full ${getEffortColor(item.effort)}`} />
                    <span className="text-xs text-gray-700">{item.effort}</span>
                  </div>
                </td>
                <td className="border-b border-orange-100 px-4 py-3 text-center text-gray-700 text-xs">{item.valeur}</td>
                <td className="border-b border-orange-100 px-4 py-3 text-center">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(item.statut)}`}>
                    {item.statut}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Contenu détaillé généré par l'IA */}
      <DocumentContent content={content} color="orange" />
    </div>
  );
}

// Composant pour le Registre des risques TI & plan de traitement
export function RegistreRisquesTIView({
  content,
  companyName,
  onEdit,
}: {
  content: string;
  companyName: string;
  onEdit: () => void;
}) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Registre_Risques_TI_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (pdfMode === 'simple') {
      await generatePDFSimple('risques-content', filename);
    } else {
      await generatePDF('risques-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'risques-content',
      `${companyName}_Registre_Risques_TI_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  // Données mock pour le visuel (sera remplacé par parsing du markdown si besoin)
  const risquesItems = [
    { id: 1, risque: 'Panne infrastructure serveurs critiques', pilier: 'Infrastructure & Cloud', probabilité: 'Élevée', impact: 'Critique', niveau: 'Critique', coût: '$500K', statut: 'En traitement' },
    { id: 2, risque: 'Brèche de données / cyberattaque', pilier: 'Sécurité & Conformité', probabilité: 'Moyenne', impact: 'Critique', niveau: 'Élevé', coût: '$1.2M', statut: 'Identifié' },
    { id: 3, risque: 'Perte de données critiques non sauvegardées', pilier: 'Applications & Données', probabilité: 'Faible', impact: 'Critique', niveau: 'Élevé', coût: '$800K', statut: 'En traitement' },
    { id: 4, risque: 'Départ clé TI / perte de connaissances', pilier: 'Gouvernance & Organisation', probabilité: 'Moyenne', impact: 'Élevé', niveau: 'Élevé', coût: '$200K', statut: 'Identifié' },
    { id: 5, risque: 'Non-conformité Loi 25 / RGPD', pilier: 'Sécurité & Conformité', probabilité: 'Élevée', impact: 'Élevé', niveau: 'Élevé', coût: '$250K', statut: 'En traitement' },
    { id: 6, risque: 'Obsolescence infrastructure', pilier: 'Infrastructure & Cloud', probabilité: 'Élevée', impact: 'Moyen', niveau: 'Moyen', coût: '$150K', statut: 'Identifié' },
    { id: 7, risque: 'Interruption de service prolongée', pilier: 'Support & Opérations', probabilité: 'Moyenne', impact: 'Élevé', niveau: 'Élevé', coût: '$300K', statut: 'Identifié' },
    { id: 8, risque: 'Dépendance fournisseur unique', pilier: 'Infrastructure & Cloud', probabilité: 'Faible', impact: 'Élevé', niveau: 'Moyen', coût: '$100K', statut: 'Accepté' },
  ];

  const parNiveau = risquesItems.reduce((acc, item) => {
    if (!acc[item.niveau]) acc[item.niveau] = [];
    acc[item.niveau].push(item);
    return acc;
  }, {} as Record<string, typeof risquesItems>);

  const getNiveauColor = (n: string) => {
    if (n === 'Critique') return 'bg-red-600 text-white border-red-700';
    if (n === 'Élevé') return 'bg-orange-500 text-white border-orange-600';
    if (n === 'Moyen') return 'bg-amber-400 text-white border-amber-500';
    return 'bg-emerald-400 text-white border-emerald-500';
  };

  const getProbabiliteColor = (p: string) => {
    if (p === 'Élevée') return 'bg-red-500';
    if (p === 'Moyenne') return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getImpactColor = (i: string) => {
    if (i === 'Critique') return 'bg-red-600';
    if (i === 'Élevé') return 'bg-orange-500';
    if (i === 'Moyen') return 'bg-amber-400';
    return 'bg-emerald-400';
  };

  const getStatusColor = (s: string) => {
    if (s === 'Traité') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (s === 'En traitement') return 'bg-blue-100 text-blue-800 border-blue-200';
    if (s === 'Accepté') return 'bg-gray-100 text-gray-800 border-gray-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  return (
    <div id="risques-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-red-600 to-rose-700 text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">Registre des risques TI & plan de traitement</h1>
            <p className="text-red-100 text-lg">{companyName}</p>
            <p className="text-red-200 text-sm mt-1">
              Identification, analyse et traitement des risques TI par pilier
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-red-500 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-red-600 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-red-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-red-700 px-5 py-2.5 rounded-lg font-semibold hover:bg-red-50 transition-all shadow-lg hover:shadow-xl text-sm"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-rose-600 px-5 py-2.5 rounded-lg font-semibold hover:bg-rose-50 transition-all shadow-lg hover:shadow-xl text-sm"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>

        {/* Stats haute niveau */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <StatCard icon={Shield} label="Total risques" value={risquesItems.length.toString()} />
          <StatCard icon={Target} label="Critiques" value={parNiveau['Critique']?.length.toString() || '0'} />
          <StatCard icon={TrendingUp} label="Élevés" value={parNiveau['Élevé']?.length.toString() || '0'} />
          <StatCard icon={CheckCircle} label="En traitement" value={risquesItems.filter(i => i.statut === 'En traitement').length.toString()} />
        </div>
      </div>

      {/* Matrice de risques visuelle */}
      <div className="bg-white rounded-2xl shadow-md border border-red-100 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Matrice de risques (Probabilité × Impact)</h2>
        <div className="grid grid-cols-4 gap-2 text-xs">
          <div className="text-right pr-2 pt-8">
            <div className="transform -rotate-90 origin-center whitespace-nowrap">Probabilité</div>
          </div>
          <div className="col-span-3">
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="text-center font-semibold text-gray-700">Faible</div>
              <div className="text-center font-semibold text-gray-700">Moyen</div>
              <div className="text-center font-semibold text-gray-700">Critique</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="h-16 bg-emerald-100 border-2 border-emerald-300 rounded flex items-center justify-center text-xs font-semibold text-emerald-800">
                Faible
              </div>
              <div className="h-16 bg-emerald-200 border-2 border-emerald-400 rounded flex items-center justify-center text-xs font-semibold text-emerald-800">
                Faible
              </div>
              <div className="h-16 bg-amber-200 border-2 border-amber-400 rounded flex items-center justify-center text-xs font-semibold text-amber-800">
                Moyen
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div className="h-16 bg-emerald-200 border-2 border-emerald-400 rounded flex items-center justify-center text-xs font-semibold text-emerald-800">
                Faible
              </div>
              <div className="h-16 bg-amber-300 border-2 border-amber-500 rounded flex items-center justify-center text-xs font-semibold text-amber-900">
                Moyen
              </div>
              <div className="h-16 bg-orange-400 border-2 border-orange-500 rounded flex items-center justify-center text-xs font-semibold text-orange-900">
                Élevé
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div className="h-16 bg-amber-300 border-2 border-amber-500 rounded flex items-center justify-center text-xs font-semibold text-amber-900">
                Moyen
              </div>
              <div className="h-16 bg-orange-500 border-2 border-orange-600 rounded flex items-center justify-center text-xs font-semibold text-white">
                Élevé
              </div>
              <div className="h-16 bg-red-600 border-2 border-red-700 rounded flex items-center justify-center text-xs font-semibold text-white">
                Critique
              </div>
            </div>
            <div className="text-center mt-2 text-xs text-gray-600">Impact →</div>
          </div>
        </div>
      </div>

      {/* Vue par niveau de risque */}
      <div className="bg-white rounded-2xl shadow-md border border-red-100 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Répartition par niveau de risque</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {['Critique', 'Élevé', 'Moyen', 'Faible'].map((niveau) => {
            const items = parNiveau[niveau] || [];
            return (
              <div key={niveau} className="border-2 rounded-lg p-4" style={{ borderColor: niveau === 'Critique' ? '#dc2626' : niveau === 'Élevé' ? '#f97316' : niveau === 'Moyen' ? '#f59e0b' : '#10b981' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold border ${getNiveauColor(niveau)}`}>
                    {niveau}
                  </span>
                  <span className="text-2xl font-bold text-gray-800">{items.length}</span>
                </div>
                <div className="space-y-1 mt-2">
                  {items.slice(0, 2).map((item) => (
                    <div key={item.id} className="text-xs text-gray-600 truncate">{item.risque}</div>
                  ))}
                  {items.length > 2 && <div className="text-xs text-gray-400">+{items.length - 2} autres</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Registre détaillé des risques */}
      <div className="bg-white rounded-2xl shadow-md border border-red-100 p-6 overflow-x-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Registre détaillé des risques par pilier TI</h2>
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-red-50">
              <th className="border-b border-red-200 px-4 py-3 text-left text-red-900 font-semibold">
                Risque
              </th>
              <th className="border-b border-red-200 px-4 py-3 text-left text-red-900 font-semibold">
                Pilier
              </th>
              <th className="border-b border-red-200 px-4 py-3 text-center text-red-900 font-semibold">
                Probabilité
              </th>
              <th className="border-b border-red-200 px-4 py-3 text-center text-red-900 font-semibold">
                Impact
              </th>
              <th className="border-b border-red-200 px-4 py-3 text-center text-red-900 font-semibold">
                Niveau
              </th>
              <th className="border-b border-red-200 px-4 py-3 text-center text-red-900 font-semibold">
                Coût potentiel
              </th>
              <th className="border-b border-red-200 px-4 py-3 text-center text-red-900 font-semibold">
                Statut
              </th>
            </tr>
          </thead>
          <tbody>
            {risquesItems.map((item, idx) => (
              <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-red-50/30'}>
                <td className="border-b border-red-100 px-4 py-3 text-gray-800 font-medium">{item.risque}</td>
                <td className="border-b border-red-100 px-4 py-3 text-gray-700 text-xs">{item.pilier}</td>
                <td className="border-b border-red-100 px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <div className={`w-3 h-3 rounded-full ${getProbabiliteColor(item.probabilité)}`} />
                    <span className="text-xs text-gray-700">{item.probabilité}</span>
                  </div>
                </td>
                <td className="border-b border-red-100 px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <div className={`w-3 h-3 rounded-full ${getImpactColor(item.impact)}`} />
                    <span className="text-xs text-gray-700">{item.impact}</span>
                  </div>
                </td>
                <td className="border-b border-red-100 px-4 py-3 text-center">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${getNiveauColor(item.niveau)}`}>
                    {item.niveau}
                  </span>
                </td>
                <td className="border-b border-red-100 px-4 py-3 text-center text-gray-700 text-xs font-semibold">{item.coût}</td>
                <td className="border-b border-red-100 px-4 py-3 text-center">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(item.statut)}`}>
                    {item.statut}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Contenu détaillé généré par l'IA */}
      <DocumentContent content={content} color="red" />
    </div>
  );
}

// Composant pour l'Offre de service BZ - Solution d'affaire holistique
export function OffreServiceBZView({
  content,
  companyName,
  onEdit,
  onEditManually,
}: {
  content: string;
  companyName: string;
  onEdit: () => void;
  onEditManually?: () => void;
}) {
  const { user } = useAuth();
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');
  
  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Offre_Service_BZ_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (pdfMode === 'simple') {
      await generatePDFSimple('offre-service-content', filename);
    } else {
      await generatePDF('offre-service-content', filename);
    }
  };

  // Fonction pour déterminer l'avatar de l'utilisateur
  const getUserAvatar = (): string | null => {
    if (!user) return null;
    
    // 1. Priorité: photo_url depuis la DB
    if (user.photo_url) {
      return user.photo_url;
    }
    
    // 2. Mapping email → fichier avatar (comme dans Header.tsx)
    const emailAvatarMap: Record<string, string> = {
      'dbourassa@bzinc.ca': '/DB.png',
      'francois.delisle@bzinc.ca': '/FP.png',
      'dany.bourassa@bzinc.ca': '/DB.png',
      'richard.delisle@bzinc.ca': '/RD.png',
      'eric.hains@bzinc.ca': '/EH.png',
      'francis.marquis@bzinc.ca': '/FM.png',
      'jean-pierre.leclerc@bzinc.ca': '/JP.png',
      'vincent.bourassa@bzinc.ca': '/VB.png',
      'jean-michel.caouette@bzinc.ca': '/JMC.png',
      'antoine.giroux@bzinc.ca': '/AG-transparent.png',
      'jean.martin@bzinc.ca': '/JM.png',
      'eric.dubois@bzinc.ca': '/ED.png',
      'marie-pier.levesque@bzinc.ca': '/MP.png',
      'equipe@bzinc.ca': '/EQ.png',
    };
    
    if (user.email && emailAvatarMap[user.email.toLowerCase()]) {
      return emailAvatarMap[user.email.toLowerCase()];
    }
    
    return null;
  };

  const userAvatar = getUserAvatar();


  // Données de repli pour anciens documents qui ne contiennent pas encore de tableau comparatif exploitable.
  const budgetMensuel = 5000; // Budget de base identifié
  
  // Données pour chaque offre avec contenu unique
  const offreEssentiel = {
    id: 'essentiel',
    nom: 'Essentielle',
    tableTitle: 'Offre ESSENTIELLE',
    description: 'Les fondamentaux TI pour démarrer',
    prix: Math.round(budgetMensuel * 0.6),
    prixLabel: `${Math.round(budgetMensuel * 0.6).toLocaleString()} $/mois`,
    piliers: 4,
    piliersLabel: '4 piliers',
    tactiques: 15,
    tactiquesLabel: '15',
    couleur: 'emerald',
    recommandee: false,
    details: [],
    piliersCouverts: ['Besoins & Alignement', 'Catalogue Actifs', 'Opérations TI', 'Cybersécurité'],
    avantages: [
      'Accès aux fondamentaux TI essentiels',
      'Budget optimisé pour PME en démarrage',
      'Support réactif heures ouvrables',
      'Sécurité de base conforme Loi 25',
    ],
    desavantages: [
      'Couverture limitée (4 piliers sur 7)',
      'Pas d\'innovation structurée',
      'Support réactif uniquement (pas proactif)',
      'Pas d\'IA ni programmation sur mesure',
    ],
    repartitionEffort: [
      { pilier: 'Besoins', pourcentage: 15, montant: 450 },
      { pilier: 'Catalogue', pourcentage: 20, montant: 600 },
      { pilier: 'Opérations', pourcentage: 35, montant: 1050 },
      { pilier: 'Cybersécurité', pourcentage: 30, montant: 900 },
    ],
    spiderData: {
      asIs: {
        'Besoins & Alignement': 30,
        'Catalogue Actifs': 35,
        'Projets & Budget': 20,
        'Opérations': 45,
        'Cybersécurité': 25,
        'Innovation': 10,
        'IA & Programmation': 5,
      },
      toBe: {
        'Besoins & Alignement': 60,
        'Catalogue Actifs': 65,
        'Projets & Budget': 40,
        'Opérations': 75,
        'Cybersécurité': 55,
        'Innovation': 20,
        'IA & Programmation': 10,
      },
    },
  };

  const offreStandard = {
    id: 'standard',
    nom: 'Recommandée',
    tableTitle: 'Offre RECOMMANDÉE',
    description: 'Solution équilibrée pour la plupart des entreprises',
    prix: budgetMensuel,
    prixLabel: `${budgetMensuel.toLocaleString()} $/mois`,
    piliers: 6,
    piliersLabel: '6 piliers',
    tactiques: 24,
    tactiquesLabel: '24',
    couleur: 'indigo',
    recommandee: true,
    details: [],
    piliersCouverts: ['Besoins & Alignement', 'Catalogue Actifs', 'Projets & Budget', 'Opérations TI', 'Cybersécurité', 'Innovation'],
    avantages: [
      'Couverture complète des piliers critiques (6/7)',
      'Équilibre optimal qualité/prix',
      'Support proactif avec monitoring prédictif',
      'Innovation structurée avec POC annuels',
      'ROI démontré sur 12-18 mois',
    ],
    desavantages: [
      'Pas d\'IA ni programmation sur mesure',
      'Innovation modérée (pas de transformation digitale majeure)',
      'Support étendu mais pas 24/7',
    ],
    repartitionEffort: [
      { pilier: 'Besoins', pourcentage: 11, montant: 550 },
      { pilier: 'Catalogue', pourcentage: 10, montant: 500 },
      { pilier: 'Projets', pourcentage: 24, montant: 1200 },
      { pilier: 'Opérations', pourcentage: 8, montant: 400 },
      { pilier: 'Cybersécurité', pourcentage: 33, montant: 1650 },
      { pilier: 'Innovation', pourcentage: 15, montant: 750 },
    ],
    spiderData: {
      asIs: {
        'Besoins & Alignement': 30,
        'Catalogue Actifs': 35,
        'Projets & Budget': 20,
        'Opérations': 45,
        'Cybersécurité': 25,
        'Innovation': 10,
        'IA & Programmation': 5,
      },
      toBe: {
        'Besoins & Alignement': 80,
        'Catalogue Actifs': 84,
        'Projets & Budget': 76,
        'Opérations': 90,
        'Cybersécurité': 80,
        'Innovation': 64,
        'IA & Programmation': 20,
      },
    },
  };

  const offrePremium = {
    id: 'premium',
    nom: 'Complète',
    tableTitle: 'Offre COMPLÈTE',
    description: 'Solution complète pour les entreprises ambitieuses',
    prix: Math.round(budgetMensuel * 1.6),
    prixLabel: `${Math.round(budgetMensuel * 1.6).toLocaleString()} $/mois`,
    piliers: 7,
    piliersLabel: '7 piliers',
    tactiques: 35,
    tactiquesLabel: '35',
    couleur: 'purple',
    recommandee: false,
    details: [],
    piliersCouverts: ['Tous les 7 piliers du Cadre BZ'],
    avantages: [
      'Couverture complète des 7 piliers BZ',
      'Support 24/7 avec SLA entreprise',
      'IA et automatisation intégrées',
      'Innovation continue avec transformation digitale',
      'Programmation sur mesure incluse',
      'ROI maximal avec avantage compétitif durable',
    ],
    desavantages: [
      'Investissement mensuel plus élevé',
      'Peut être surdimensionné pour petites PME',
      'Complexité accrue nécessite engagement client',
    ],
    repartitionEffort: [
      { pilier: 'Besoins', pourcentage: 10, montant: 800 },
      { pilier: 'Catalogue', pourcentage: 8, montant: 640 },
      { pilier: 'Projets', pourcentage: 20, montant: 1600 },
      { pilier: 'Opérations', pourcentage: 12, montant: 960 },
      { pilier: 'Cybersécurité', pourcentage: 28, montant: 2240 },
      { pilier: 'Innovation', pourcentage: 12, montant: 960 },
      { pilier: 'IA & Programmation', pourcentage: 10, montant: 800 },
    ],
    spiderData: {
      asIs: {
        'Besoins & Alignement': 30,
        'Catalogue Actifs': 35,
        'Projets & Budget': 20,
        'Opérations': 45,
        'Cybersécurité': 25,
        'Innovation': 10,
        'IA & Programmation': 5,
      },
      toBe: {
        'Besoins & Alignement': 90,
        'Catalogue Actifs': 94,
        'Projets & Budget': 86,
        'Opérations': 96,
        'Cybersécurité': 90,
        'Innovation': 80,
        'IA & Programmation': 76,
      },
    },
  };

  const offres: OffreServiceOption[] = extractOffreServiceOptions(content) || ([offreEssentiel, offreStandard, offrePremium] as OffreServiceOption[]);
  const offreRecommandee = offres.find((offre) => offre.recommandee) || offres[1] || offreStandard;
  const formatOffrePrice = (offre: OffreServiceOption) =>
    offre.prix != null ? `${offre.prix.toLocaleString()} $` : offre.prixLabel;
  const comparisonRows = [
    {
      label: 'Investissement mensuel',
      values: offres.map(formatOffrePrice),
    },
    {
      label: 'Intention',
      values: offres.map((offre) => offre.description),
    },
    {
      label: 'Piliers couverts',
      values: offres.map((offre) => offre.piliersLabel),
    },
    {
      label: 'Tactiques activées',
      values: offres.map((offre) => offre.tactiquesLabel),
    },
    ...Array.from(
      new Set(
        offres
          .flatMap((offre) => offre.details.map((detail) => detail.label))
          .filter((label) => label && !/intention|prix mensuel|piliers couverts/i.test(label))
      )
    )
      .slice(0, 8)
      .map((label) => ({
        label,
        values: offres.map((offre) => offre.details.find((detail) => detail.label === label)?.value || '—'),
      })),
  ];

  return (
    <div id="offre-service-content" className="max-w-6xl mx-auto space-y-8 bg-gray-50 pb-12">
      {/* Header avec logo BZ et numéro ODS */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            {userAvatar ? (
              <div className="w-16 h-16 rounded-lg overflow-hidden ring-2 ring-indigo-200 shadow-lg flex-shrink-0">
                <img 
                  src={userAvatar} 
                  alt={user?.name || 'Utilisateur'} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback vers logo BZ si l'image ne charge pas
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.className = 'w-16 h-16 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-xl flex-shrink-0';
                      parent.textContent = 'BZ';
                    }
                  }}
                />
              </div>
            ) : (
              <div className="w-16 h-16 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                BZ
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">Offre de Service Technologique</h1>
              <p className="text-gray-600 text-sm">ODS N° ODS-{Math.floor(Math.random() * 900000) + 100000} | Généré le {new Date().toLocaleDateString('fr-CA')}</p>
              <p className="text-gray-700 font-semibold mt-1">BZ Inc. - Solutions TI sur mesure</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-purple-700 transition-all text-sm"
            >
              <Sparkles className="w-4 h-4" />
              Éditer avec IA
            </button>
            {onEditManually && (
              <button
                onClick={onEditManually}
                className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-all text-sm border border-gray-300"
              >
                <Edit2 className="w-4 h-4" />
                Modifier manuellement
              </button>
            )}
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-indigo-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-all text-sm"
              >
                <Download className="w-4 h-4" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={async () => {
                await generateHTML(
                  'offre-service-content',
                  `${companyName}_Offre_Service_BZ_${new Date().toISOString().split('T')[0]}.html`
                );
              }}
              className="flex items-center gap-2 bg-white text-indigo-600 border border-indigo-300 px-4 py-2 rounded-lg font-semibold hover:bg-indigo-50 transition-all text-sm"
            >
              <Download className="w-4 h-4" />
              Télécharger HTML
            </button>
          </div>
        </div>

        {/* Message d'introduction */}
        <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-100 mb-6">
          <p className="text-gray-800 leading-relaxed">
            <span className="font-semibold">Cher {companyName},</span>
            <br />
            <br />
            Nous vous présentons cette offre de service technologique, élaborée avec soin pour répondre à vos besoins spécifiques.
            Chez BZ, nous comprenons l'importance cruciale des technologies de l'information pour la croissance et le succès de votre entreprise.
            Cette proposition est basée sur une approche holistique structurée autour des piliers TI prioritaires qui garantissent une transformation
            numérique complète et alignée avec vos objectifs d'affaires.
          </p>
        </div>

        {/* Profil Client */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-100">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Profil Client</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">Entreprise</p>
              <p className="font-semibold text-gray-900">{companyName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Secteur</p>
              <p className="font-semibold text-gray-900">Technologie</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Taille</p>
              <p className="font-semibold text-gray-900">10-25 employés</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Budget base</p>
              <p className="font-semibold text-gray-900">{budgetMensuel.toLocaleString()} $ CAD/mois</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Niveau de risque</p>
              <p className="font-semibold text-gray-900">Moyen</p>
            </div>
          </div>
        </div>
      </div>

      {/* Vue d'ensemble avec Spider Chart Macro (Offre Recommandée) */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Vue d'Ensemble - Maturité TI Cible</h2>
            <p className="text-gray-600 mt-1">Analyse AS-IS vs TARGET pour l'offre {offreRecommandee.nom} (recommandée)</p>
          </div>
          <div className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-lg font-semibold">
            ⭐ Offre Recommandée
          </div>
        </div>
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
          <SpiderChartComparison
            asIs={offreRecommandee.spiderData.asIs}
            toBe={offreRecommandee.spiderData.toBe}
          />
          <div className="mt-4 text-center text-sm text-gray-700">
            <p className="font-semibold mb-2">Progression attendue avec l'offre {offreRecommandee.nom}:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <div className="text-lg font-bold text-indigo-700">{offreRecommandee.piliers ?? '—'}</div>
                <div className="text-xs text-gray-600">Piliers couverts</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <div className="text-lg font-bold text-indigo-700">{offreRecommandee.tactiquesLabel}</div>
                <div className="text-xs text-gray-600">Tactiques activées</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <div className="text-lg font-bold text-indigo-700">
                  {offreRecommandee.prix != null ? `${offreRecommandee.prix.toLocaleString()} $` : offreRecommandee.prixLabel}
                </div>
                <div className="text-xs text-gray-600">Investissement mensuel</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <div className="text-lg font-bold text-indigo-700">12-18 mois</div>
                <div className="text-xs text-gray-600">Horizon ROI</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Synthèse des 3 offres */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Nos 3 offres adaptées à votre budget</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {offres.map((offre) => {
            const colorMap: Record<string, string> = {
              emerald: 'text-emerald-600',
              indigo: 'text-indigo-600',
              purple: 'text-purple-600',
            };
            return (
              <div
                key={offre.id}
                className={`relative border-2 rounded-xl p-6 transition-all hover:shadow-lg ${
                  offre.recommandee
                    ? 'border-indigo-500 bg-indigo-50 shadow-lg'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {offre.recommandee && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-indigo-600 text-white px-4 py-1 rounded-full text-xs font-bold">
                    ⭐ RECOMMANDÉE
                  </div>
                )}
                <div className="text-center mb-4">
                  <Star className={`w-12 h-12 mx-auto mb-3 ${colorMap[offre.couleur] || 'text-gray-600'}`} />
                  <h3 className={`text-xl font-bold mb-2 ${offre.recommandee ? 'text-indigo-900' : 'text-gray-900'}`}>
                    {offre.nom}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">{offre.description}</p>
                  <div className="text-3xl font-bold text-gray-800 mb-1">
                    {offre.prix != null ? `${offre.prix.toLocaleString()} $ CAD` : offre.prixLabel}
                    <span className="text-sm font-normal text-gray-600 block">par mois</span>
                  </div>
                </div>
                <div className="space-y-2 mt-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Piliers:</span>
                    <span className="font-semibold">{offre.piliers != null ? `${offre.piliers} / 6` : offre.piliersLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tactiques:</span>
                    <span className="font-semibold">{offre.tactiquesLabel}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Présentation détaillée de chaque offre */}
      {offres.map((offre) => {
        const colorMap: Record<string, { bg: string; text: string; border: string; progress: string }> = {
          emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', progress: 'bg-emerald-500' },
          indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', progress: 'bg-indigo-500' },
          purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', progress: 'bg-purple-500' },
        };
        const colors = colorMap[offre.couleur] || colorMap.indigo;
        
        return (
          <div key={offre.id} className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 ${colors.bg} rounded-lg flex items-center justify-center`}>
                  <Star className={`w-8 h-8 ${colors.text}`} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Offre {offre.nom}
                    {offre.recommandee && <span className="ml-3 text-indigo-600 text-lg">⭐ Recommandée</span>}
                  </h2>
                  <p className="text-gray-600 mt-1">{offre.description}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-gray-900">
                  {offre.prix != null ? `${offre.prix.toLocaleString()} $ CAD` : offre.prixLabel}
                </div>
                <div className="text-sm text-gray-600">par mois</div>
              </div>
            </div>

            {/* Métriques clés */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                <div className="text-2xl font-bold text-gray-900">{offre.piliers ?? '—'}</div>
                <div className="text-xs text-gray-600 mt-1">Piliers couverts</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                <div className="text-2xl font-bold text-gray-900">{offre.tactiquesLabel}</div>
                <div className="text-xs text-gray-600 mt-1">Tactiques activées</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                <div className="text-2xl font-bold text-gray-900">{offre.piliersCouverts.length}</div>
                <div className="text-xs text-gray-600 mt-1">Piliers principaux</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                <div className="text-2xl font-bold text-gray-900">12-18</div>
                <div className="text-xs text-gray-600 mt-1">Mois ROI</div>
              </div>
            </div>

            {/* Piliers couverts */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Piliers du Cadre BZ couverts</h3>
              <div className="flex flex-wrap gap-2">
                {offre.piliersCouverts.map((pilier, idx) => (
                  <span key={idx} className={`px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
                    {pilier}
                  </span>
                ))}
              </div>
            </div>

            {/* Répartition de l'effort */}
            {offre.repartitionEffort.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition de l'investissement mensuel</h3>
              <div className="space-y-3">
                {offre.repartitionEffort.map((item, idx) => {
                  const pilierColors: Record<string, string> = {
                    'Besoins': 'bg-blue-500',
                    'Catalogue': 'bg-emerald-500',
                    'Projets': 'bg-purple-500',
                    'Opérations': 'bg-orange-500',
                    'Cybersécurité': 'bg-red-500',
                    'Innovation': 'bg-cyan-500',
                    'IA & Programmation': 'bg-violet-500',
                  };
                  return (
                    <div key={idx} className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="font-semibold text-gray-800">{item.pilier}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-gray-600">{item.pourcentage}%</span>
                          <span className="font-bold text-gray-900">{item.montant.toLocaleString()} $</span>
                        </div>
                      </div>
                      <div className="relative h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`absolute left-0 top-0 h-full ${pilierColors[item.pilier] || 'bg-gray-500'} rounded-full transition-all duration-1000`}
                          style={{ width: `${item.pourcentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {/* Détails extraits du tableau comparatif */}
            <div className={offre.desavantages.length > 0 ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : 'grid grid-cols-1 gap-6'}>
              <div>
                <h3 className="text-lg font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Détails de l'offre
                </h3>
                <div className="space-y-2">
                  {offre.avantages.map((avantage, idx) => (
                    <div key={idx} className="bg-emerald-50 rounded-lg p-3 border border-emerald-200 flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-800">{avantage}</span>
                    </div>
                  ))}
                </div>
              </div>
              {offre.desavantages.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-orange-900 mb-3 flex items-center gap-2">
                  <X className="w-5 h-5 text-orange-600" />
                  Points à considérer
                </h3>
                <div className="space-y-2">
                  {offre.desavantages.map((desavantage, idx) => (
                    <div key={idx} className="bg-orange-50 rounded-lg p-3 border border-orange-200 flex items-start gap-2">
                      <X className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-800">{desavantage}</span>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Tableau comparatif final */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Comparatif des 3 offres</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-indigo-50">
                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Critère</th>
                {offres.map((offre) => (
                  <th
                    key={offre.id}
                    className={`border border-gray-300 px-4 py-3 text-center font-semibold ${
                      offre.recommandee ? 'text-indigo-700 bg-indigo-100' : offre.couleur === 'emerald' ? 'text-emerald-700' : 'text-purple-700'
                    }`}
                  >
                    {offre.tableTitle.replace(/^Offre\s+/i, '')}
                    {offre.recommandee ? ' ⭐' : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, rowIdx) => (
                <tr key={row.label} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">{row.label}</td>
                  {offres.map((offre, idx) => (
                    <td
                      key={`${offre.id}-${row.label}`}
                      className={`border border-gray-300 px-4 py-3 text-center text-sm ${
                        offre.recommandee ? 'bg-indigo-50 font-semibold' : ''
                      }`}
                    >
                      {row.values[idx] || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-indigo-50 rounded-lg p-4 border border-indigo-200">
          <p className="text-sm text-gray-800">
            <strong>Recommandation BZ:</strong> L'offre <strong>{offreRecommandee.nom}</strong> est mise de l'avant comme le meilleur équilibre risque/coût selon les données de l'ODS.
          </p>
        </div>
      </div>

      {/* Nos Services & Recommandations BZ */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
        <div className="flex items-center gap-2 mb-6">
          <Target className="w-6 h-6 text-emerald-600" />
          <h2 className="text-2xl font-bold text-gray-900">Nos Services & Recommandations BZ</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Vos Objectifs Identifiés */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Vos Objectifs Identifiés</h3>
            <div className="space-y-3">
              {['Amélioration de la sécurité', 'Conformité réglementaire', 'Digitalisation des processus'].map((objectif, idx) => (
                <div key={idx} className="bg-blue-50 rounded-lg p-4 border border-blue-200 flex items-center gap-3">
                  <Target className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-800 font-medium">{objectif}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Notre Approche BZ */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notre Approche BZ</h3>
            
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Approche Holistique</h4>
              <p className="text-sm text-gray-700">
                Basée sur 3 axes: <strong>Humain, Processus et Technologie</strong>. Nos 4 piliers essentiels: 
                Planification, Gestion, Accompagnement, Soutien.
              </p>
            </div>

            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
              <h4 className="font-semibold text-emerald-900 mb-2">Notre Mission</h4>
              <p className="text-sm text-gray-700">
                Prendre en charge les opérations TI avec des solutions durables, efficientes et à la fine pointe de la technologie, 
                axées sur la sécurité.
              </p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <h4 className="font-semibold text-purple-900 mb-2">Nos Expertises</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Infogérance TI complète</li>
                <li>Cybersécurité & Conformité Loi 25</li>
                <li>Solutions infonuagiques (BZ Cloud + Azure)</li>
                <li>Programmation & IA sur mesure</li>
                <li>Téléphonie Cloud</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Prochaines Étapes Recommandées */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Prochaines Étapes Recommandées</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <div className="text-3xl font-bold text-blue-600 mb-2">1.</div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Analyser</h3>
            <p className="text-sm text-gray-700">
              Évaluation exhaustive de votre environnement, besoins et capacités actuelles.
            </p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-6 border border-emerald-200">
            <div className="text-3xl font-bold text-emerald-600 mb-2">2.</div>
            <h3 className="text-lg font-semibold text-emerald-900 mb-2">Recommander</h3>
            <p className="text-sm text-gray-700">
              Solutions efficientes alignées avec vos objectifs d'affaires et budget.
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
            <div className="text-3xl font-bold text-purple-600 mb-2">3.</div>
            <h3 className="text-lg font-semibold text-purple-900 mb-2">Implanter</h3>
            <p className="text-sm text-gray-700">
              Déploiement progressif avec accompagnement complet de nos experts.
            </p>
          </div>
        </div>
      </div>

      {/* Call-to-Action */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl shadow-xl p-8 text-white">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Prêt à transformer votre infrastructure TI?</h2>
            <p className="text-indigo-100">
              Notre équipe d'experts vous accompagne à chaque étape de votre transformation numérique.
            </p>
          </div>
          <div className="text-right">
            <p className="font-semibold mb-1">Antoine Giroux</p>
            <p className="text-sm text-indigo-100 mb-1">Directeur des ventes</p>
            <div className="flex items-center gap-2 text-sm mb-1">
              <Phone className="w-4 h-4" />
              <span>418-688-2727, poste 233</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4" />
              <span>antoine.giroux@bzinc.ca</span>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu détaillé généré par l'IA */}
      <DocumentContent content={content} color="indigo" />
    </div>
  );
}

// Composant pour un axe stratégique
function AxeCard({ icon, title, description, progress, color }: { 
  icon: React.ReactNode; 
  title: string; 
  description: string; 
  progress: number;
  color: string;
}) {
  const colorClasses: Record<string, { bg: string; text: string; bar: string }> = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', bar: 'bg-blue-500' },
    red: { bg: 'bg-red-50', text: 'text-red-600', bar: 'bg-red-500' },
    green: { bg: 'bg-green-50', text: 'text-green-600', bar: 'bg-green-500' },
    yellow: { bg: 'bg-yellow-50', text: 'text-yellow-600', bar: 'bg-yellow-500' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', bar: 'bg-purple-500' },
  };

  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <div className={`${colors.bg} border-2 border-${color}-200 rounded-lg p-6 hover:shadow-lg transition-shadow`}>
      <div className={`${colors.text} mb-4`}>
        {icon}
      </div>
      <h4 className="font-bold text-gray-900 text-lg mb-2">{title}</h4>
      <p className="text-gray-600 text-sm mb-4">{description}</p>
      
      {/* Barre de progression */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Maturité actuelle</span>
          <span className={`font-bold ${colors.text}`}>{progress}%</span>
        </div>
        <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={`${colors.bar} h-3 rounded-full transition-all duration-1000`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}

// Composant pour l'ODS
function ODSView({ content, companyName, onEdit }: { content: string; companyName: string; onEdit: () => void }) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');
  const [tocPageNumbers, setTocPageNumbers] = useState<Record<string, number>>({});
  const pdfContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const container = pdfContentRef.current;
      if (!container) return;
      const headings = container.querySelectorAll<HTMLElement>('[data-doc-heading]');
      const rect = container.getBoundingClientRect();
      const next: Record<string, number> = {};
      headings.forEach((el) => {
        const title = el.getAttribute('data-doc-heading');
        if (!title) return;
        const r = el.getBoundingClientRect();
        const offset = r.top - rect.top + container.scrollTop;
        const page = 1 + Math.max(0, Math.floor(offset / PDF_PAGE_HEIGHT_PX));
        next[title] = page;
      });
      setTocPageNumbers((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    }, 100);
    return () => clearTimeout(t);
  }, [content]);

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Offre_de_Service_${new Date().toISOString().split('T')[0]}.pdf`;
    if (pdfMode === 'simple') {
      await generatePDFSimple('ods-pdf-content', filename, {
        headerFooter: { logoUrl: '/BZ.png', clientName: companyName, documentTitle: 'Offre de Service (ODS) – BZ Inc.' },
      });
    } else {
      await generatePDF('ods-pdf-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'ods-pdf-content',
      `${companyName}_Offre_de_Service_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  return (
    <div id="ods-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section (non incluse dans le PDF) */}
      <div className="bg-gradient-to-br from-orange-600 to-red-600 text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2">Offre de Service (ODS)</h1>
            <p className="text-orange-100 text-lg">{companyName}</p>
            <p className="text-orange-200 text-sm mt-2">Services et niveaux de support</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-orange-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-all shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>

        {/* Service metrics */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <StatCard icon={Target} label="Services" value="8+" />
          <StatCard icon={Clock} label="Support 24/7" value="Oui" />
          <StatCard icon={TrendingUp} label="SLA" value="99.5%" />
          <StatCard icon={CheckCircle} label="KPIs" value="15+" />
        </div>
      </div>

      {/* Contenu formaté (seule cette partie est incluse dans le PDF) */}
      <div id="ods-pdf-content" ref={pdfContentRef}>
        {/* En-tête PDF / contenu avec bandeau bleu et nom du client */}
        <div
          className="rounded-2xl mb-8 px-10 py-8 text-white"
          style={{
            background: 'linear-gradient(90deg, #406fb4 0%, #28518d 100%)',
          }}
        >
          <p className="text-xs tracking-[0.25em] uppercase opacity-80 mb-3">
            Offre de service
          </p>
          <h1 className="text-4xl font-extrabold leading-tight mb-2">
            {companyName}
          </h1>
          <p className="text-sm opacity-90">BZ Inc.</p>
        </div>

        <DocumentContent content={content} color="orange" tocPageNumbers={tocPageNumbers} />
      </div>
    </div>
  );
}

type EbaucheOdsSourceMetadata = {
  id?: number;
  template_id?: string;
  nom?: string;
  created_at?: string;
  updated_at?: string;
} | null;

const EBAUCHE_ODS_PILLAR_COLORS = [
  'bg-indigo-500',
  'bg-sky-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-violet-500',
];

const EBAUCHE_ODS_PILLAR_LABELS = [
  'Gouvernance & stratégie',
  'Architecture & cloud',
  'Applications & données',
  'Services & opérations',
  'Cybersécurité',
  'Transformation & IA',
];

const normalizeEbaucheOdsPillarTitle = (title: string, idx: number) =>
  title
    ? title.replace(/^Pilier\s*\d+\s*[—-]\s*/i, '').replace(/\s*\([^)]*\)\s*$/g, '').trim()
    : EBAUCHE_ODS_PILLAR_LABELS[idx] || `Pilier ${idx + 1}`;

const kmiScoreToPercent = (score: number) => Math.round(Math.max(0, Math.min(5, score)) * 20);

const buildEbaucheOdsMaturityData = (
  pillars: Array<{ pillarTitle?: string; averageScore?: number; countedAnswers?: number }>
) => {
  const source = pillars.length > 0 ? pillars : EBAUCHE_ODS_PILLAR_LABELS.map((pillarTitle) => ({ pillarTitle, averageScore: 0, countedAnswers: 0 }));

  return source.slice(0, 6).reduce(
    (acc, pillar, idx) => {
      const label = normalizeEbaucheOdsPillarTitle(String(pillar.pillarTitle || ''), idx);
      const asIs = pillar.countedAnswers ? kmiScoreToPercent(Number(pillar.averageScore || 0)) : 0;
      const target = asIs > 0 ? Math.min(95, Math.max(65, asIs + 35)) : 70;
      acc.asIs[label] = asIs;
      acc.toBe[label] = target;
      return acc;
    },
    { asIs: {} as Record<string, number>, toBe: {} as Record<string, number> }
  );
};

export function EbaucheOdsView({
  content,
  companyName,
  onEdit,
  onEditManually,
  metadata,
}: {
  content: string;
  companyName: string;
  onEdit: () => void;
  onEditManually?: () => void;
  metadata?: any;
}) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');
  const sources: Record<string, EbaucheOdsSourceMetadata> = metadata?.sources || {};
  const kmiPillars: Array<{ pillarTitle?: string; averageScore?: number; countedAnswers?: number }> =
    Array.isArray(metadata?.ebauche_ods?.kmi_pillars) ? metadata.ebauche_ods.kmi_pillars : [];
  const maturityData = buildEbaucheOdsMaturityData(kmiPillars);
  const pricingTableTotals = [
    ...extractEbaucheOdsPricingTableTotals(content),
    ...extractEbaucheOdsAdditionalProductTotals(content),
  ];
  const budgetReference = pricingTableTotals.reduce((sum, table) => sum + table.total, 0) || null;
  const elementOfferOptions = extractOffreServiceOptions(content);
  const scenarioComparativeTable = elementOfferOptions ? null : extractEbaucheOdsComparativeTable(content);
  const extractedOfferCards = elementOfferOptions || mapEbaucheOdsRowsToOfferOptions(scenarioComparativeTable) || [];
  const offerCards = applyBudgetReferenceToOfferCards(extractedOfferCards, budgetReference);

  const sourceCards = [
    { key: 'ods', label: 'Prise de besoin - ODS', role: 'Portée, services, tarification' },
    { key: 'client2026', label: 'Client 2026', role: 'Enjeux, priorités, risques' },
    { key: 'kmi', label: 'Prisme - KMI', role: 'Maturité par pilier' },
  ];

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_Ebauche_ODS_${new Date().toISOString().split('T')[0]}.pdf`;
    if (pdfMode === 'simple') {
      await generatePDFSimple('ebauche-ods-content', filename, {
        headerFooter: { logoUrl: '/BZ.png', clientName: companyName, documentTitle: "Ébauche d'ODS – BZ Inc." },
      });
    } else {
      await generatePDF('ebauche-ods-content', filename);
    }
  };

  const handleDownloadHTML = async () => {
    await generateHTML('ebauche-ods-content', `${companyName}_Ebauche_ODS_${new Date().toISOString().split('T')[0]}.html`);
  };

  return (
    <div id="ebauche-ods-content" className="max-w-6xl mx-auto space-y-7 bg-slate-50 pb-10">
      <div className="rounded-2xl overflow-hidden shadow-lg border border-indigo-100 bg-white">
        <div className="bg-gradient-to-r from-slate-950 via-indigo-800 to-emerald-700 px-8 py-7 text-white">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-emerald-200 font-semibold">Gestion documentaire PRIsME</p>
              <h1 className="mt-2 text-3xl font-bold">Ébauche d&apos;ODS</h1>
              <p className="mt-2 text-sm text-indigo-100 max-w-2xl">
                Analyse croisée des formulaires ODS, Client 2026 et KMI selon les 6 piliers du cadre client BZ.
              </p>
              <p className="mt-3 text-sm font-semibold text-white">{companyName}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={onEdit}
                className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/20 hover:bg-white/20"
              >
                <Sparkles className="w-4 h-4" />
                Éditer avec IA
              </button>
              {onEditManually && (
                <button
                  onClick={onEditManually}
                  className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-indigo-50"
                >
                  <Edit2 className="w-4 h-4" />
                  Modifier
                </button>
              )}
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="rounded-lg border border-white/20 bg-white/95 px-3 py-2 text-xs text-slate-800"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-400 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-300"
              >
                <Download className="w-4 h-4" />
                PDF
              </button>
              <button
                onClick={handleDownloadHTML}
                className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/20 hover:bg-white/20"
              >
                <Download className="w-4 h-4" />
                HTML
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 p-6 md:grid-cols-3">
          {sourceCards.map((source) => {
            const sourceMeta = sources[source.key];
            const date = sourceMeta?.updated_at || sourceMeta?.created_at;
            return (
              <div key={source.key} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold text-slate-900">{source.label}</p>
                    <p className="mt-1 text-[11px] text-slate-600">{source.role}</p>
                  </div>
                  <CheckCircle2 className={`h-5 w-5 ${sourceMeta ? 'text-emerald-500' : 'text-slate-300'}`} />
                </div>
                <p className="mt-3 text-[11px] font-medium text-slate-700">{sourceMeta?.nom || 'Source non enregistrée'}</p>
                <p className="mt-1 text-[10px] text-slate-500">{date ? new Date(date).toLocaleDateString('fr-CA') : 'Date non disponible'}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-md">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-950">Vue d&apos;ensemble - Maturité TI cible</h2>
            <p className="mt-1 text-sm text-slate-600">
              Analyse AS-IS issue du KMI vs TARGET recommandé pour une ébauche d&apos;ODS à valider.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 self-start rounded-lg bg-indigo-100 px-4 py-2 text-sm font-bold text-indigo-800">
            <Star className="h-4 w-4 fill-amber-300 text-amber-400" />
            Offre recommandée
          </div>
        </div>
        <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50 p-6">
          <SpiderChartComparison asIs={maturityData.asIs} toBe={maturityData.toBe} />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-md">
        {offerCards.length > 0 && (
          <>
            <h2 className="mb-6 text-center text-2xl font-bold text-slate-950">Nos 3 offres adaptées à votre budget</h2>
            {budgetReference && (
              <div className="mx-auto mb-6 max-w-xl rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Budget de référence détecté</p>
                <p className="mt-1 text-lg font-bold text-slate-950">
                  {budgetReference.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $ CAD / mois
                </p>
                <p className="mt-1 text-xs text-slate-600">Total calculé à partir des tableaux de montants de l&apos;Ébauche d&apos;ODS.</p>
              </div>
            )}
            <div className="grid gap-6 md:grid-cols-3">
              {offerCards.map((offer) => {
            const colorMap: Record<string, { icon: string; border: string; bg: string; title: string }> = {
              emerald: { icon: 'text-emerald-500', border: 'border-emerald-200', bg: 'bg-white', title: 'text-slate-950' },
              indigo: { icon: 'text-indigo-600', border: 'border-indigo-500', bg: 'bg-indigo-50', title: 'text-indigo-950' },
              purple: { icon: 'text-purple-600', border: 'border-purple-200', bg: 'bg-white', title: 'text-slate-950' },
            };
            const colors = colorMap[offer.couleur] || colorMap.indigo;

            return (
              <div
                key={offer.id}
                className={`relative rounded-xl border-2 ${colors.border} ${colors.bg} p-6 text-center shadow-sm`}
              >
                {offer.recommandee && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-indigo-600 px-4 py-1 text-[11px] font-bold uppercase text-white">
                    Recommandée
                  </div>
                )}
                <Star className={`mx-auto mb-4 h-12 w-12 ${colors.icon}`} />
                <h3 className={`mb-2 text-xl font-bold ${colors.title}`}>{offer.nom}</h3>
                <p className="mx-auto mb-5 min-h-[40px] max-w-[240px] text-sm text-slate-600">{offer.description}</p>
                <div className="mb-5 text-3xl font-bold text-slate-900">
                  {offer.prix != null ? `${offer.prix.toLocaleString('fr-CA')} $ CAD` : offer.prixLabel}
                  <span className="block text-sm font-normal text-slate-600">par mois</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Piliers:</span>
                    <span className="font-bold text-slate-900">{offer.piliers != null ? `${offer.piliers} / 6` : offer.piliersLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Tactiques:</span>
                    <span className="font-bold text-slate-900">{offer.tactiquesLabel}</span>
                  </div>
                </div>
                <p className="mt-5 text-left text-[11px] leading-relaxed text-slate-500">
                  {offer.details.find((detail) => /services/i.test(normalizeKey(detail.label)))?.value || 'Données du tableau comparatif.'}
                </p>
              </div>
            );
              })}
            </div>
          </>
        )}
      </div>

      {kmiPillars.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-950">Lecture KMI par pilier</h2>
              <p className="mt-1 text-xs text-slate-600">Scores issus du formulaire Prisme - KMI, échelle 0 à 5.</p>
            </div>
            <BarChart3 className="h-6 w-6 text-indigo-600" />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {kmiPillars.map((pillar, idx) => {
              const score = Number(pillar.averageScore || 0);
              const pct = Math.max(0, Math.min(100, (score / 5) * 100));
              const title = normalizeEbaucheOdsPillarTitle(String(pillar.pillarTitle || ''), idx);
              return (
                <div key={`${title}-${idx}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className={`h-3 w-3 rounded-full ${EBAUCHE_ODS_PILLAR_COLORS[idx % EBAUCHE_ODS_PILLAR_COLORS.length]}`} />
                      <p className="text-sm font-semibold text-slate-900">{title}</p>
                    </div>
                    <span className="text-sm font-bold text-slate-950">{score ? `${score.toFixed(1)} / 5` : 'N/A'}</span>
                  </div>
                  <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className={`h-full rounded-full ${EBAUCHE_ODS_PILLAR_COLORS[idx % EBAUCHE_ODS_PILLAR_COLORS.length]}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="mt-2 text-[10px] text-slate-500">{pillar.countedAnswers || 0} réponse(s) comptée(s)</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <DocumentContent content={content} color="indigo" budgetReference={budgetReference} />
      </div>
    </div>
  );
}

// Composant générique pour les documents futurs avec menu PDF
export function GenericDocumentView({ 
  content, 
  companyName, 
  onEdit,
  onEditManually,
  pdfHeader,
  documentTitle,
  documentTypeCode,
  metadata,
  color = 'blue'
}: { 
  content: string; 
  companyName: string; 
  onEdit: () => void;
  onEditManually?: () => void;
  pdfHeader?: React.ReactNode;
  documentTitle?: string;
  documentTypeCode?: string;
  metadata?: Record<string, any>;
  color?: string;
}) {
  const [pdfMode, setPdfMode] = useState<'simple' | 'avance'>('simple');
  const [tocPageNumbers, setTocPageNumbers] = useState<Record<string, number>>({});
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const hasCyberIncidentCover = !!extractCyberIncidentCoverData(content);

  useEffect(() => {
    const t = setTimeout(() => {
      const container = pdfContentRef.current;
      if (!container) return;
      const headings = container.querySelectorAll<HTMLElement>('[data-doc-heading]');
      const rect = container.getBoundingClientRect();
      const next: Record<string, number> = {};
      headings.forEach((el) => {
        const title = el.getAttribute('data-doc-heading');
        if (!title) return;
        const r = el.getBoundingClientRect();
        const offset = r.top - rect.top + container.scrollTop;
        const page = 1 + Math.max(0, Math.floor(offset / PDF_PAGE_HEIGHT_PX));
        next[title] = page;
      });
      setTocPageNumbers((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    }, 100);
    return () => clearTimeout(t);
  }, [content]);

  const handleDownloadPDF = async () => {
    const filename = `${companyName}_${documentTitle || 'Document'}_${new Date().toISOString().split('T')[0]}.pdf`;
    const shouldLandscape = documentTypeCode === 'PRISE_BESOIN_CLIENT_2026';
    const shouldA3 = documentTypeCode === 'PRISE_BESOIN_CLIENT_2026';
    if (pdfMode === 'simple') {
      await generatePDFSimple('generic-document-pdf-content', filename, {
        orientation: shouldLandscape ? 'landscape' : 'portrait',
        format: shouldA3 ? 'a3' : 'letter',
        headerFooter: hasCyberIncidentCover
          ? undefined
          : { logoUrl: '/BZ.png', clientName: companyName, documentTitle: documentTitle ? `${documentTitle} – BZ Inc.` : 'Document – BZ Inc.' },
      });
    } else {
      await generatePDF('generic-document-pdf-content', filename, {
        orientation: shouldLandscape ? 'landscape' : 'portrait',
        format: shouldA3 ? 'a3' : 'a4',
        fit: shouldA3 ? 'width' : undefined,
      });
    }
  };

  const extractFirstMarkdownTable = (raw: string): { headers: string[]; rows: string[][] } | null => {
    if (!raw) return null;
    const body = stripCyberIncidentCoverBlock(raw);
    const lines = body.split('\n');
    const isTableLine = (ln: string) => /^\|.*\|$/.test(ln.trim());

    for (let i = 0; i < lines.length - 1; i++) {
      if (isTableLine(lines[i]) && isTableLine(lines[i + 1])) {
        const tableLines: string[] = [];
        while (i < lines.length && isTableLine(lines[i])) {
          tableLines.push(lines[i].trim());
          i++;
        }
        if (tableLines.length < 2) return null;
        const parseRow = (ln: string) => documentTypeCode === 'PRISE_BESOIN_CLIENT_2026'
          ? parseClient2026MarkdownRow(ln)
          : ln
              .trim()
              .replace(/^\|/, '')
              .replace(/\|$/, '')
              .split('|')
              .map((c) => c.trim());
        const headers = parseRow(tableLines[0]);
        const rows = tableLines.slice(2).map(parseRow); // skip separator row
        return { headers, rows };
      }
    }
    return null;
  };

  const normalizeTableKey = (value: string) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const buildClient2026Workbook = (table: { headers: string[]; rows: string[][] }) => {
    const workbook = XLSX.utils.book_new();
    const isLegacyClient2026Document = metadata?.client2026_schema !== 2 && metadata?.client2026_schema !== 3;
    const isV3Client2026Document = metadata?.client2026_schema === 2;
    const currentRows = isV3Client2026Document ? CLIENT_2026_V3_ROWS : CLIENT_2026_ROWS;
    const columnDefs = isLegacyClient2026Document
      ? LEGACY_CLIENT_2026_ALL_COL_DEFS
      : CLIENT_2026_ALL_COL_DEFS;
    const headers = columnDefs.map((col) => col.label);
    const normalizedHeaders = table.headers.map(normalizeTableKey);
    const questionHeaderIndex = normalizedHeaders.findIndex((h) =>
      h.includes('problemes/besoins/souhaits') || h === normalizeTableKey('Question')
    );
    const axeHeaderIndex = normalizedHeaders.indexOf(normalizeTableKey('Axe'));

    const rowByQuestion = new globalThis.Map<string, string[]>();
    if (questionHeaderIndex >= 0) {
      table.rows.forEach((row) => {
        const question = normalizeTableKey(row[questionHeaderIndex] || '');
        if (!question) return;
        const axe = axeHeaderIndex >= 0 ? normalizeTableKey(row[axeHeaderIndex] || '') : '';
        if (axe) rowByQuestion.set(`${axe}|${question}`, row);
        if (!rowByQuestion.has(question)) rowByQuestion.set(question, row);
      });
    }

    const valueFromMarkdown = (colLabel: string, sourceRow?: string[]) => {
      if (!sourceRow) return '';
      const idx = normalizedHeaders.indexOf(normalizeTableKey(colLabel));
      return idx >= 0 ? String(sourceRow[idx] ?? '').trim() : '';
    };

    const buildCurrentRows = (rows = currentRows) => rows.map((def) => {
      const sourceRow = isV3Client2026Document
        ? findClient2026V3MarkdownRow(def, rowByQuestion, normalizeTableKey)
        : findClient2026MarkdownRow(def, rowByQuestion, normalizeTableKey);
      return CLIENT_2026_ALL_COL_DEFS.map((col) =>
        isV3Client2026Document
          ? getClient2026V3MarkdownColumnValue(def, col, (label) => valueFromMarkdown(label, sourceRow))
          : getClient2026MarkdownColumnValue(def, col, (label) => valueFromMarkdown(label, sourceRow))
      );
    });

    const buildLegacyRows = (rows = LEGACY_CLIENT_2026_ROWS) => rows.map((def) => {
      const sourceRow = findLegacyClient2026MarkdownRow(def, rowByQuestion, normalizeTableKey);
      return buildLegacyClient2026ExportRow(def, (label) => valueFromMarkdown(label, sourceRow));
    });

    const addSheet = (sheetName: string, rows: string[][]) => {
      const data = isLegacyClient2026Document ? [headers, ...rows] : [...buildClient2026WorkbookHeaders(), ...rows];
      const sheet = XLSX.utils.aoa_to_sheet(data);
      if (!isLegacyClient2026Document) sheet['!merges'] = CLIENT_2026_WORKBOOK_MERGES;
      sheet['!cols'] = columnDefs.map((col) => ({
        wch: Math.min(55, Math.max(12, Math.round(col.label.length * 0.85))),
      }));
      sheet['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: data.length - 1, c: headers.length - 1 } }) };
      XLSX.utils.book_append_sheet(workbook, sheet, sheetName.substring(0, 31));
    };

    addSheet('Toutes_les_phases', isLegacyClient2026Document ? buildLegacyRows() : buildCurrentRows());

    const axisNumbers = Array.from(new Set(
      (isLegacyClient2026Document ? LEGACY_CLIENT_2026_ROWS : currentRows).map((row) => row.axe),
    ));
    axisNumbers.forEach((axe) => {
      const rowsForAxe = isLegacyClient2026Document
        ? buildLegacyRows(LEGACY_CLIENT_2026_ROWS.filter((row) => row.axe === axe))
        : buildCurrentRows(currentRows.filter((row) => row.axe === axe));
      addSheet(`Axe ${axe}`, rowsForAxe);
    });

    const axisTitles = isLegacyClient2026Document ? LEGACY_CLIENT_2026_AXE_TITLES : CLIENT_2026_AXE_TITLES;
    const infoSheet = XLSX.utils.aoa_to_sheet([
      ['Export PRIsME - Prise de besoin - Maturité et besoins client'],
      ['Contenu', isLegacyClient2026Document
        ? 'Export fidèle du questionnaire historique (version 2).'
        : 'Tous les axes, toutes les questions et toutes les colonnes A à AE, incluant les barèmes de maturité.'],
      ['Colonnes incluses', columnDefs.map((col) => `${col.key}: ${col.label}`).join(' | ')],
      ['Axes inclus', Object.entries(axisTitles).map(([axe, title]) => `${axe}: ${title}`).join(' | ')],
    ]);
    infoSheet['!cols'] = [{ wch: 28 }, { wch: 140 }];
    XLSX.utils.book_append_sheet(workbook, infoSheet, 'Informations');

    return workbook;
  };

  const handleDownloadExcel = async () => {
    const table = extractFirstMarkdownTable(content);
    if (!table) {
      alert('Aucun tableau détecté dans ce document.');
      return;
    }
    if (documentTypeCode === 'PRISE_BESOIN_CLIENT_2026') {
      const wb = buildClient2026Workbook(table);
      const filename = `${companyName}_${documentTitle || 'Document'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      return;
    }
    const aoa = [table.headers, ...table.rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    (ws as any)['!cols'] = table.headers.map((h) => ({
      wch: Math.min(70, Math.max(14, Math.round((h?.length || 14) * 0.9))),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Prise_de_besoin_2026');
    const filename = `${companyName}_${documentTitle || 'Document'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const handleDownloadHTML = async () => {
    await generateHTML(
      'generic-document-pdf-content',
      `${companyName}_${documentTitle || 'Document'}_${new Date().toISOString().split('T')[0]}.html`
    );
  };

  return (
    <div id="generic-document-content" className="max-w-5xl mx-auto space-y-8">
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-2xl p-8 shadow-xl">
        <div className={`flex justify-between ${hasCyberIncidentCover ? 'items-center' : 'items-start mb-6'} gap-6 flex-wrap`}>
          {!hasCyberIncidentCover && (
            <div>
              <h1 className="text-4xl font-bold mb-2">
                {(documentTitle || 'Document').replace(/\s*\(\d{4}-\d{2}-\d{2}\)\s*$/, '')}
              </h1>
              <p className="text-blue-100 text-lg">{companyName}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            {onEditManually && (
              <button
                onClick={onEditManually}
                className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
              >
                <Edit2 className="w-5 h-5" />
                Modifier manuellement
              </button>
            )}
            <div className="flex items-center gap-2">
              <select
                value={pdfMode}
                onChange={(e) => setPdfMode(e.target.value as 'simple' | 'avance')}
                className="text-xs border border-blue-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                title="Choisir le mode de génération PDF"
              >
                <option value="simple">PDF Simple</option>
                <option value="avance">PDF Avancé</option>
              </select>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5" />
                Télécharger PDF
              </button>
              {documentTypeCode === 'PRISE_BESOIN_CLIENT_2026' && (
                <button
                  onClick={handleDownloadExcel}
                  className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl"
                >
                  <Download className="w-5 h-5" />
                  Télécharger Excel
                </button>
              )}
            </div>
            <button
              onClick={handleDownloadHTML}
              className="flex items-center gap-2 bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl"
            >
              <Download className="w-5 h-5" />
              Télécharger HTML
            </button>
          </div>
        </div>
      </div>

      {/* Contenu formaté (seule cette partie est incluse dans le PDF) */}
      <div id="generic-document-pdf-content" ref={pdfContentRef}>
        {pdfHeader ? <div className="mb-8">{pdfHeader}</div> : null}
        <DocumentContent content={content} color={color} tocPageNumbers={tocPageNumbers} />
      </div>
    </div>
  );
}

// Hauteur logique d'une page PDF (A4 avec marges) en px, pour calcul des numéros de page
const PDF_PAGE_HEIGHT_PX = Math.round((277 / 25.4) * 96);

// Composant pour afficher le contenu formaté
export function DocumentContent({
  content,
  color,
  tocPageNumbers,
  budgetReference,
}: {
  content: string;
  color: string;
  tocPageNumbers?: Record<string, number>;
  budgetReference?: number | null;
}) {
  const cyberCover = extractCyberIncidentCoverData(content);
  const contentForBody = stripCyberIncidentCoverBlock(content);

  // Nettoyer le contenu d'abord - enlever TOUS les markdown et placeholders
  const cleanContent = contentForBody
    .replace(/\*\*\*(.+?)\*\*\*/g, '$1') // Gras+Italique
    .replace(/\*\*(.+?)\*\*/g, '$1') // Gras
    .replace(/\*(.+?)\*/g, '$1') // Italique
    .replace(/__(.+?)__/g, '$1') // Underscore gras
    .replace(/^\\(?=#{1,6}\s*)/gm, '') // Titres échappés "\# ..." générés par l'IA
    .replace(/\s*_\s*_\s*_\s*/g, '') // placeholders " _ _ _ "
    .replace(/_{2,}/g, ''); // placeholders "___"
  
  const lines = cleanContent.split('\n');
  const elements: React.JSX.Element[] = [];
  let key = 0;
  let inToc = false;

  const isTableLine = (ln: string) => /^\|.*\|$/.test(ln.trim());
  const isTableDesMatieres = (t: string) =>
    t.toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').trim() === 'table des matières';

  /** Enlève les numéros de section en début de titre (ex. "1.", "2.1.", "1) ") */
  const stripSectionNumber = (raw: string) => raw.replace(/^\s*\d+(\.\d+)*[.)]\s*/, '').trim();

  /** Bloc fixe section Signature (sans coordonnées du responsable) — paragraphe encadré en rouge à l’origine, sous le titre */
  const renderSignatureBlock = () => {
    const k = key++;
    return (
      <div key={k} className="mt-6 prisme-avoid-break space-y-6">
        <p className="text-gray-700 leading-relaxed">
          Le Client, agissant en son nom propre et/ou par le biais de son représentant dûment habilité, atteste avoir pris
          connaissance, compris et reçu, le cas échéant, toutes les explications nécessaires relatives aux termes énoncés
          dans le présent accord. De plus, il reconnaît et accepte l'ensemble des dispositions de cet accord.
        </p>
        <div className="flex flex-wrap items-baseline gap-2 text-gray-700">
          <span>Signé à</span>
          <span className="inline-block min-w-[200px] border-b border-gray-400" style={{ minHeight: '1.25em' }} aria-hidden />
          <span>, le</span>
          <span className="inline-block min-w-[120px] border-b border-gray-400" style={{ minHeight: '1.25em' }} aria-hidden />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          <div className="space-y-4">
            <div className="bg-amber-100/80 px-2 py-1 rounded text-gray-900 font-medium text-sm inline-block">Client</div>
            <div className="min-h-[2rem] border-b border-gray-400 w-full" aria-hidden />
            <div className="text-gray-800 font-medium">BZ inc.</div>
            <div className="min-h-[2rem] border-b border-gray-400 w-full" aria-hidden />
          </div>
          <div className="space-y-4">
            <div className="bg-amber-100/80 px-2 py-1 rounded text-gray-900 font-medium text-sm inline-block">Nom du responsable</div>
            <div className="min-h-[2rem] border-b border-gray-400 w-full" aria-hidden />
            <div className="bg-amber-100/80 px-2 py-1 rounded text-gray-900 font-medium text-sm inline-block mt-2">Titre</div>
            <div className="min-h-[2rem] border-b border-gray-400 w-full" aria-hidden />
          </div>
        </div>
      </div>
    );
  };

  /** Saut de page sans créer de page blanche : force la page suivante sans espace vide */
  const pageBreakSpacer = (_pages = 1) => (
    <div
      key={key++}
      className="prisme-page-break"
      style={{
        height: 0,
        minHeight: 0,
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        pageBreakBefore: 'always',
        flexShrink: 0,
      }}
      aria-hidden
    />
  );

  if (cyberCover) {
    elements.push(
      <div
        key={key++}
        className="w-full mb-0 prisme-avoid-break prisme-cyber-incident-cover overflow-hidden"
        style={{
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          aspectRatio: '791 / 1024',
          backgroundImage: 'url("/cyber-incident-cover-hires.png")',
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
        }}
      >
        <div className="absolute inset-x-0 top-[4.8%] px-10">
          <div
            className="text-center font-normal"
            style={{
              color: '#B9C2D0',
              fontSize: 'clamp(1.2rem, 2.1vw, 2rem)',
              lineHeight: 1.2,
            }}
          >
            {cyberCover.title ?? 'Rapport d’incident de cybersécurité'}
          </div>
        </div>
        {/* Client + date: même flux vertical pour éviter tout chevauchement. */}
        <div className="absolute inset-x-0 top-[11.2%] flex justify-center px-10">
          <div className="flex flex-col items-center text-center" style={{ maxWidth: '84%' }}>
            <div
              className="text-white font-medium tracking-tight"
              style={{
                fontSize: 'clamp(2.1rem, 4vw, 3.7rem)',
                lineHeight: 1.08,
                textTransform: 'uppercase',
                wordBreak: 'break-word',
                maxWidth: '100%',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
              }}
            >
              {cyberCover.client}
            </div>
            <div
              className="font-normal"
              style={{
                color: '#AEB8C9',
                fontSize: 'clamp(1.45rem, 2.15vw, 2.25rem)',
                lineHeight: 1.15,
                maxWidth: '70%',
                marginTop: 'clamp(0.4rem, 0.9vw, 0.8rem)',
              }}
            >
              {cyberCover.dateLabel}
            </div>
          </div>
        </div>
      </div>
    );
    elements.push(pageBreakSpacer());
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim().replace(/^\\(?=#{1,6}\s*)/, '');
    
    if (!line) continue;

    // Détection de SVG inline (commence par <svg)
    if (line.startsWith('<svg')) {
      let svgContent = '';
      let svgLineIndex = i;
      
      // Collecter tout le SVG jusqu'à </svg>
      while (svgLineIndex < lines.length) {
        svgContent += lines[svgLineIndex] + '\n';
        if (lines[svgLineIndex].includes('</svg>')) {
          break;
        }
        svgLineIndex++;
      }
      
      // Avancer l'index principal
      i = svgLineIndex;
      
      // Nettoyer le SVG (enlever les espaces en début/fin)
      svgContent = svgContent.trim();
      
      // Valider et corriger le viewBox si nécessaire
      if (!svgContent.includes('viewBox="0 0 1280 720"')) {
        svgContent = svgContent.replace(
          /<svg([^>]*)>/,
          '<svg$1 viewBox="0 0 1280 720" width="100%" height="auto" xmlns="http://www.w3.org/2000/svg">'
        );
      }
      
      elements.push(
        <div 
          key={key++} 
          className="my-8 flex justify-center overflow-x-auto prisme-avoid-break"
          style={{ maxWidth: '100%' }}
          dangerouslySetInnerHTML={{ __html: svgContent }} 
        />
      );
      
      continue;
    }

    // Détection simple de tableaux Markdown (lignes qui commencent et finissent par |)
    if (isTableLine(line) && i + 1 < lines.length && isTableLine(lines[i + 1])) {
      const tableLines: string[] = [];
      while (i < lines.length && isTableLine(lines[i])) {
        tableLines.push(lines[i].trim());
        i++;
      }
      i--;

      if (tableLines.length >= 2) {
        const headerCells = tableLines[0]
          .split('|')
          .slice(1, -1)
          .map((c) => c.trim());

        const bodyRows = tableLines.slice(2).map((row) =>
          row
            .split('|')
            .slice(1, -1)
            .map((c) => c.trim())
        );

        const norm = (h: string) => h.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/\s+/g, ' ');
        const hasItem = headerCells.some((h) => norm(h).includes('item'));
        const hasUnitPrice = headerCells.some((h) => norm(h).includes('prix') && norm(h).includes('unitaire'));
        const hasUnits = headerCells.some((h) => norm(h).includes('unites') || norm(h).includes('unite'));
        const hasTotal = headerCells.some((h) => norm(h).includes('total'));
        const isPricingTable = hasItem && hasUnitPrice && hasUnits && hasTotal;
        const catIdx = headerCells.findIndex((h) => norm(h).includes('categorie'));
        const formatPricingCell = (raw: string): string => {
          const s = (raw || '').trim().replace(/\s/g, '').replace(/\$/g, '').replace(',', '.');
          const n = parseFloat(s);
          if (!Number.isFinite(n)) return raw || '';
          return `${n.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
        };
        const unitPriceIdx = headerCells.findIndex((h) => norm(h).includes('prix') && norm(h).includes('unitaire'));
        const totalIdx = headerCells.findIndex((h) => norm(h).includes('total'));

        if (isPricingTable) {
          const itemIdx = headerCells.findIndex((h) => norm(h).includes('item') || norm(h).includes('titre'));
          const isTotalRow = (row: string[]) => {
            const label = normalizeKey(row[itemIdx] || row[0] || '');
            return label === 'total' || label.includes('total mensuel') || label.includes('total des montants');
          };
          const sourceRows = bodyRows.filter((row) => !isTotalRow(row));
          const explicitTotalRow = bodyRows.find(isTotalRow);
          const computedTotal = sourceRows.reduce((sum, row) => {
            const value = parseCurrencyValue(row[totalIdx] || '');
            return value == null ? sum : sum + value;
          }, 0);
          const explicitTotal = explicitTotalRow ? parseCurrencyValue(explicitTotalRow[totalIdx] || '') : null;
          const pricingTotal = explicitTotal != null ? explicitTotal : computedTotal;
          const groups: { category: string; rows: string[][] }[] = [];
          let current: { category: string; rows: string[][] } | null = null;
          sourceRows.forEach((row) => {
            const catVal = (row[catIdx] || '').trim();
            if (catVal && (!current || current.category !== catVal)) {
              current = { category: catVal, rows: [] };
              groups.push(current);
            }
            if (!catVal && !current) {
              current = { category: '', rows: [] };
              groups.push(current);
            }
            if (current) current.rows.push(row);
          });

          elements.push(
            <div key={key++} className="my-4 overflow-x-auto prisme-avoid-break border border-gray-200 rounded-lg bg-white">
              <table className="w-full text-[11px] text-gray-700 border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    {headerCells.map((cell, idx) => (
                      <th key={idx} className="px-3 py-2 text-left font-semibold border-b border-gray-200 text-gray-800">
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groups.map((grp, gIdx) => (
                    <React.Fragment key={gIdx}>
                      {grp.category ? (
                        <tr className="bg-gray-100">
                          <td colSpan={headerCells.length} className="px-3 py-2 font-semibold text-gray-700">
                            {grp.category}
                          </td>
                        </tr>
                      ) : null}
                      {grp.rows.map((row, rIdx) => (
                        <tr key={`${gIdx}-${rIdx}`} className="border-b border-gray-100 last:border-b-0">
                          {headerCells.map((_, cIdx) => {
                            const isCatCol = cIdx === catIdx;
                            const isUnitPrice = cIdx === unitPriceIdx;
                            const isTotal = cIdx === totalIdx;
                            const raw = (row[cIdx] || '').trim();
                            const display = isCatCol ? '' : (isUnitPrice || isTotal ? formatPricingCell(raw) : raw);
                            return (
                              <td
                                key={cIdx}
                                className={`px-3 py-2 ${isCatCol ? 'bg-gray-50' : ''} text-gray-700`}
                              >
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
                {pricingTotal > 0 && (
                  <tfoot>
                    <tr className="bg-emerald-50">
                      {headerCells.map((_, cIdx) => {
                        const display =
                          cIdx === itemIdx
                            ? 'Total mensuel'
                            : cIdx === totalIdx
                              ? formatPricingCell(String(pricingTotal))
                              : '';
                        return (
                          <td
                            key={cIdx}
                            className={`border-t border-emerald-200 px-3 py-2 font-bold ${
                              cIdx === totalIdx ? 'text-right text-emerald-800' : 'text-slate-900'
                            }`}
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          );
        } else {
          const normalizedHeaders = headerCells.map(normalizeKey);
          const productTypeIdx = normalizedHeaders.findIndex((header) => header.includes('type'));
          const productTitleIdx = normalizedHeaders.findIndex((header) => header.includes('titre'));
          const productContentIdx = normalizedHeaders.findIndex((header) => header.includes('contenu'));
          const productStatusIdx = normalizedHeaders.findIndex((header) => header.includes('statut'));
          const isAdditionalProductsTable =
            productTypeIdx >= 0 && productTitleIdx >= 0 && productContentIdx >= 0 && productStatusIdx >= 0;
          const isAdditionalProductTotalRow = (row: string[]) => {
            const label = normalizeKey(row[productTypeIdx] || row[productTitleIdx] || '');
            return label === 'total' || label.includes('total mensuel');
          };
          const additionalProductRows = isAdditionalProductsTable
            ? bodyRows.filter((row) => !isAdditionalProductTotalRow(row))
            : bodyRows;
          const additionalProductsTotal = isAdditionalProductsTable
            ? additionalProductRows.reduce((sum, row) => {
                const value = parseMonthlyAmountFromText(row[productContentIdx] || '');
                return value == null ? sum : sum + value / 12;
              }, 0)
            : 0;
          const displayRows = applyBudgetReferenceToComparativeRows(headerCells, bodyRows, budgetReference);
          elements.push(
            <div key={key++} className="my-4 overflow-x-auto prisme-avoid-break">
              <table className="min-w-full border-collapse text-[11px] rounded-lg overflow-hidden shadow-sm">
                <thead>
                  <tr>
                    {headerCells.map((cell, idx) => (
                      <th
                        key={idx}
                        className="px-3 py-2 border-b border-gray-300 bg-gray-100 text-left font-semibold text-gray-800"
                      >
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(isAdditionalProductsTable ? additionalProductRows : displayRows).map((row, rIdx) => (
                    <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {row.map((cell, cIdx) => {
                        const trimmed = cell.trim();
                        const isRaciLetter = ['R', 'A', 'C', 'I'].includes(trimmed);

                        let raciClass = '';
                        if (isRaciLetter) {
                          if (trimmed === 'R') raciClass = 'bg-emerald-600 text-white';
                          else if (trimmed === 'A') raciClass = 'bg-blue-600 text-white';
                          else if (trimmed === 'C') raciClass = 'bg-amber-500 text-white';
                          else if (trimmed === 'I') raciClass = 'bg-gray-200 text-gray-800';
                        }

                        return (
                          <td
                            key={cIdx}
                            className={`px-3 py-1.5 border-b border-gray-200 text-[11px] ${
                              isRaciLetter ? 'text-center' : 'text-gray-700'
                            }`}
                          >
                            {isRaciLetter ? (
                              <span
                                className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${raciClass}`}
                              >
                                {trimmed}
                              </span>
                            ) : (
                              trimmed
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                {isAdditionalProductsTable && additionalProductsTotal > 0 && (
                  <tfoot>
                    <tr className="bg-emerald-50">
                      {headerCells.map((_, cIdx) => {
                        const display =
                          cIdx === productTypeIdx
                            ? 'Total mensuel'
                            : cIdx === productContentIdx
                              ? `${additionalProductsTotal.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`
                              : '';
                        return (
                          <td
                            key={cIdx}
                            className={`border-t border-emerald-200 px-3 py-2 font-bold ${
                              cIdx === productContentIdx ? 'text-emerald-800' : 'text-slate-900'
                            }`}
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          );
        }

        continue;
      }
    }

    // Titres H3 avec ### (vérifier en premier car plus spécifique)
    if (line.startsWith('###')) {
      const rawText = line.replace(/^#{1,6}\s*/, '').trim();
      const textWithoutNumber = stripSectionNumber(rawText);
      const withoutTrailingParenthesis = textWithoutNumber.replace(/\s*\([^)]*\)\s*$/, '').trim();
      const normalized = withoutTrailingParenthesis.toLowerCase();

      // Ne pas afficher de titre "Lettre d'introduction"
      if (normalized === "lettre d'introduction" || normalized === "lettre d’introduction") {
        continue;
      }

      inToc = false;
      elements.push(
        <h3
          key={key++}
          id={slug(withoutTrailingParenthesis)}
          data-doc-heading={withoutTrailingParenthesis}
          className="text-xl font-semibold text-gray-800 mt-6 mb-3"
        >
          {withoutTrailingParenthesis}
        </h3>
      );
    }
    // Titres H2 avec ##
    else if (line.startsWith('##')) {
      const rawText = line.replace(/^#{1,6}\s*/, '').trim();
      const textWithoutNumber = stripSectionNumber(rawText);
      const withoutTrailingParenthesis = textWithoutNumber.replace(/\s*\([^)]*\)\s*$/, '').trim();
      const normalized = withoutTrailingParenthesis.toLowerCase();

      // Ne pas afficher de titre "Lettre d'introduction"
      if (normalized === "lettre d'introduction" || normalized === "lettre d’introduction") {
        continue;
      }

      const isToc = isTableDesMatieres(withoutTrailingParenthesis);
      if (isToc) elements.push(pageBreakSpacer(1));
      else if (inToc) {
        elements.push(pageBreakSpacer(2));
        inToc = false;
      }
      inToc = isToc;
      elements.push(
        <h2
          key={key++}
          id={slug(withoutTrailingParenthesis)}
          data-doc-heading={withoutTrailingParenthesis}
          className="text-2xl font-bold text-gray-900 mt-8 mb-4 flex items-center gap-3"
        >
          <div className="w-2 h-8 rounded-full" style={{ backgroundColor: getColor(color) }} />
          {withoutTrailingParenthesis}
        </h2>
      );
      if (normalized === 'signature') {
        elements.push(renderSignatureBlock());
        while (i + 1 < lines.length && !lines[i + 1].trim().startsWith('#')) i++;
      }
    }
    // Titres H1 avec #
    else if (line.startsWith('#')) {
      const rawText = line.replace(/^#{1,6}\s*/, '').trim();
      const textWithoutNumber = stripSectionNumber(rawText);
      const withoutTrailingParenthesis = textWithoutNumber.replace(/\s*\([^)]*\)\s*$/, '').trim();
      const normalized = withoutTrailingParenthesis.toLowerCase();

      // Ne pas afficher de titre "Lettre d'introduction"
      if (normalized === "lettre d'introduction" || normalized === "lettre d’introduction") {
        continue;
      }

      const isTocH1 = isTableDesMatieres(withoutTrailingParenthesis);
      if (isTocH1) elements.push(pageBreakSpacer(1));
      else if (inToc) {
        elements.push(pageBreakSpacer(2));
        inToc = false;
      }
      inToc = isTocH1;
      elements.push(
        <h1
          key={key++}
          id={slug(withoutTrailingParenthesis)}
          data-doc-heading={withoutTrailingParenthesis}
          className="text-3xl font-bold text-gray-900 mt-10 mb-6 pb-3 border-b-4"
          style={{ borderColor: getColor(color) }}
        >
          {withoutTrailingParenthesis}
        </h1>
      );
      if (normalized === 'signature') {
        elements.push(renderSignatureBlock());
        while (i + 1 < lines.length && !lines[i + 1].trim().startsWith('#')) i++;
      }
    }
    // Listes avec - ou • ou *
    else if (line.match(/^[-•*]\s/)) {
      const item = line.replace(/^[-•*]\s*/, '').trim();
      elements.push(
        <div key={key++} className="flex items-start gap-3 ml-4 my-2 prisme-avoid-break">
          <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: getColor(color) }} />
          <p className="text-gray-700 leading-relaxed flex-1">{item}</p>
        </div>
      );
    }
    // Listes numérotées
    else if (/^\d+\.\s/.test(line)) {
      const match = line.match(/^(\d+)\.\s*(.+)$/);
      if (match) {
        const titleRaw = match[2].trim();
        const titleNorm = titleRaw.replace(/\s*\([^)]*\)\s*$/, '').trim();
        const pageNum = tocPageNumbers?.[titleNorm];

        if (inToc) {
          elements.push(
            <div key={key++} className="flex items-baseline gap-2 py-1.5 border-b border-gray-100 border-dotted prisme-avoid-break">
              <span className="text-gray-800 flex-shrink-0">{titleNorm || titleRaw}</span>
              <span className="flex-1 min-w-2 border-b border-dotted border-gray-300 self-end mb-1" style={{ minHeight: 2 }} aria-hidden />
              <span className="text-gray-600 text-sm flex-shrink-0 tabular-nums">p. {pageNum ?? '—'}</span>
            </div>
          );
        } else {
          elements.push(
            <div key={key++} className="flex items-start gap-3 ml-4 my-2 prisme-avoid-break">
              <div className="flex items-center justify-center w-7 h-7 rounded-full text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: getColor(color) }}>
                {match[1]}
              </div>
              <p className="text-gray-700 leading-relaxed flex-1">{match[2]}</p>
            </div>
          );
        }
      }
    }
    // Paragraphe normal
    else {
      elements.push(
        <p key={key++} className="text-gray-700 leading-relaxed my-3 prisme-avoid-break">
          {line}
        </p>
      );
    }
  }
  
  return (
    <div className={cyberCover ? 'bg-white' : 'bg-white rounded-xl shadow-lg p-8'}>
      <div className={cyberCover ? 'max-w-none' : 'prose prose-lg max-w-none'}>
        {elements}
      </div>
      {!cyberCover && (
        <div className="mt-12 pt-6 border-t-2 border-gray-200">
          <div className="bg-white rounded-xl p-2 shadow-lg border-2 border-gray-200 inline-block">
            <img src="/BZ.png" alt="BZ Logo" className="w-12 h-12 object-contain" />
          </div>
        </div>
      )}
    </div>
  );
}

function getColor(_color: string): string {
  // Palette unifiée : tous les accents (bleus/oranges/etc.) utilisent ce bleu corporatif
  const corporateBlue = '#406fb4';
  return corporateBlue;
}

/** Slug pour ancres / ids (titres de section) */
function slug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'section';
}

// Composants utilitaires
function StatCard({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
      <Icon className="w-6 h-6 text-white mb-2" />
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-white/80 text-xs">{label}</div>
    </div>
  );
}
